import { normalizeDocument, documentId } from './documentIdentity';
// API client with offline support
import axios from 'axios';
import { toast } from 'sonner';
import { pullAllPages } from './paginatedSync';
import { installerPasserelle, TYPE_REQUETE_EN_FILE } from './offline/gateway';
import { attendreEcranLibre, ceder, definirDonneesLocalesPretes, donneesLocalesSontPretes } from './offline/arrierePlan';
import { rejouerRequete, liberer } from './offline/replay';
import { fusionnerPageDuPull, reconcilierSuppressions } from './offline/synchro';
import { noterSynchroLocale, purgerDerives } from './offline/store';
import { telechargerListes, telechargerUniques, telechargerDetails } from './offline/telechargement';
import { fetchAllPages } from './fetchAllPages';
import { filtrerTrajetsLocalement } from './filtresTrajets';
import { messageErreur } from './erreurs';
import { arrondiDevise } from './formats';
import { signalerEcriture } from './fraicheur';
import {
  syncQueue as syncQueueDB,
  fullSync,
  trucksDB,
  driversDB,
  companiesDB,
  operationsDB,
  tripsDB,
  tripUpdatesDB,
  clientsDB,
  ordersDB,
  invoicesDB,
  stockMovementsDB,
  stationsDB,
  stationTanksDB,
  stationPumpsDB,
  stationAttendantsDB,
  stationPricesDB,
  stationPurchasesDB,
  stationSalesDB,
  stationShiftsDB,
  stationExpensesDB,
  appState,
} from './db';

// Use explicit REACT_APP_BACKEND_URL if set at build time, otherwise use a relative path
// so axios inherits the current protocol (https) from the browser — avoids mixed-content errors.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api/v1` : '/api/v1';

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const getApiBaseUrl = () => API_BASE;

// Thin wrapper over the raw queue: every enqueue (28 call sites use the
// try-online/fallback-queue pattern) notifies the UI right away, instead of the
// counter lagging up to 10s behind the user's action.
const syncQueue = {
  ...syncQueueDB,
  async add(entityType, action, data) {
    const item = await syncQueueDB.add(entityType, action, data);
    if (item) notifyPendingChange();
    return item;
  },
};

// Le jeton part avec CHAQUE requête, lu au moment de l'envoi. Il n'était posé
// sur `api.defaults` que par l'effet de restauration de session du fournisseur
// d'authentification — or React exécute les effets des enfants avant ceux du
// parent: au rechargement d'une page, ses premiers appels (camions, sociétés,
// résumé des alertes) partaient sans en-tête et revenaient en 403, puis
// réussissaient une fraction de seconde plus tard.
export const attachStoredToken = (config) => {
  const headers = config.headers || {};
  const already = typeof headers.get === 'function' ? headers.get('Authorization') : headers.Authorization;
  if (!already) {
    const token = getAuthToken() || localStorage.getItem('token');
    if (token) {
      if (typeof headers.set === 'function') headers.set('Authorization', `Bearer ${token}`);
      else headers.Authorization = `Bearer ${token}`;
      config.headers = headers;
    }
  }
  return config;
};
api.interceptors.request.use(attachStoredToken);

// Global error handler with toast notifications
api.interceptors.response.use(
  (response) => {
    // Toute écriture réussie (y compris le rejeu de la file hors ligne, qui
    // passe par POST /sync) prévient les écrans abonnés: voir lib/fraicheur.js.
    signalerEcriture(response?.config);
    return response;
  },
  async (error) => {
    // La phrase française de l'opérateur, calculée une fois ici (voir
    // `lib/erreurs.js`): jamais le message anglais d'axios, jamais le texte brut
    // du serveur. Les pages la relisent avec `messageErreur(error, repli)`.
    const message = messageErreur(error);
    const status = error.response?.status;
    error.messageOperateur = message;

    // Don't show toast for login errors (handled in AuthContext)
    if (error.config?.url?.includes('/login')) {
      return Promise.reject(error);
    }

    // Clear auth token on 401 — but only once refreshing has genuinely failed.
    if (status === 401) {
      const original = error.config || {};
      const isRefreshCall = String(original.url || '').includes('/auth/refresh');
      if (!isRefreshCall && !original._retriedAfterRefresh) {
        const fresh = await refreshAccessToken();
        if (fresh) {
          original._retriedAfterRefresh = true;
          original.headers = { ...(original.headers || {}), Authorization: `Bearer ${fresh}` };
          // Replay the request the user actually made: they should never see
          // that the token turned over underneath them.
          return api.request(original);
        }
      }
      if (refreshSansReseau) {
        refreshSansReseau = false;
        return Promise.reject(error);
      }
      setAuthToken(null);
      setRefreshToken(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.reload();
    }

    // Un refus qui dit ce qui manque ne va pas dans un toast.
    //
    // Le backend pose depuis longtemps `details.operator_message` — la phrase
    // en français d'exploitant — et `details.missing` — la liste de ce qui
    // manque. L'intercepteur les jetait, affichait le `message` technique
    // pendant quatre secondes, et l'information disparaissait avant que
    // l'utilisateur ait fini de la lire. Elle part maintenant vers le bandeau
    // « À réparer », qui reste à l'écran tant que ce n'est pas corrigé.
    //
    // L'événement passe par le DOM plutôt que par un import: le bandeau
    // téléverse, donc il importe déjà ce module, et un import en retour
    // fermerait le cycle.
    // L'événement est annulable: c'est le bandeau qui dit « je l'ai pris ».
    // Sans cet accusé, un refus déclenché depuis un écran qui n'affiche pas
    // de bandeau ne produirait plus rien du tout — un silence pire que le
    // toast qu'il remplace. Non pris, le message retombe donc sur le toast.
    const details =
      error.response?.data?.error?.details || error.response?.data?.details || null;
    const missing = Array.isArray(details?.missing) ? details.missing : [];
    let routedToBanner = false;

    if (
      details &&
      (details.operator_message || missing.length > 0) &&
      typeof window !== 'undefined' &&
      window.dispatchEvent
    ) {
      const event = new CustomEvent('ecarbu:a-reparer', {
        cancelable: true,
        detail: {
          rule: details.rule || null,
          trip_id: details.trip_id || null,
          operator_message: details.operator_message || message,
          missing,
          message,
          details,
        },
      });
      routedToBanner = !window.dispatchEvent(event);
    }

    // Show toast error
    if (message && !error.config?.skipToast && !routedToBanner) {
      // L'identifiant égal au texte évite le double toast quand la page
      // toaste le même message à son tour (voir components/ui/sonner.jsx).
      toast.error(message, { id: message });
    }

    return Promise.reject(error);
  }
);

// Erreur « le serveur ne répond pas »: ce qui n'a pas de version locale échoue
// ainsi, sans rien mettre en file.
const networkError = () => {
  const error = new Error('Network Error');
  error.code = 'ERR_NETWORK';
  return error;
};

const getTotalFromHeaders = (response) => {
  const total = response?.headers?.['x-total-count'];
  if (total === undefined || total === null) return null;
  const parsed = Number.parseInt(total, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Décide si un échec de lecture autorise le repli sur le cache hors-ligne.
 *
 * Le repli n'a de sens que si le serveur n'a rien répondu: coupure réseau,
 * DNS, timeout. Dès qu'une réponse HTTP existe — 401 expiré, 403 de module,
 * 422 de paramètre, 500 — le serveur *a* parlé, et servir à sa place un cache
 * local vide transforme son refus en « aucune donnée ». C'est exactement ce
 * qui rendait la liste de clients vide dans la création de commande: l'écran
 * ne pouvait pas distinguer « zéro client » d'un appel refusé.
 *
 * L'erreur est journalisée avec son objet: le message seul ne disait ni le
 * code, ni l'URL, ni le détail renvoyé par le serveur.
 */
const shouldFallbackOffline = (error, label) => {
  const status = error?.response?.status;
  if (status) {
    console.error(`Server refused ${label} (HTTP ${status})`, {
      url: error?.config?.url,
      params: error?.config?.params,
      detail: error?.response?.data,
    });
    throw error;
  }
  console.warn(`Falling back to local data for ${label}`, error?.message || error);
  return true;
};

// Auth token management
let authToken = null;

export const setAuthToken = (token) => {
  authToken = token;
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('authToken', token);
  } else {
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('authToken');
  }
};

export const getAuthToken = () => {
  if (!authToken) {
    authToken = localStorage.getItem('authToken');
    if (authToken) {
      api.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
    }
  }
  return authToken;
};

export const setRefreshToken = (token) => {
  if (token) localStorage.setItem('refreshToken', token);
  else localStorage.removeItem('refreshToken');
};

export const getRefreshToken = () => localStorage.getItem('refreshToken');

// Access tokens used to live forever; they now expire after twelve hours. Without
// this, every back-office user would be thrown back to the login screen twice a
// day, mid-form, with no way to recover — the 401 handler below simply wiped the
// session and reloaded the page.
//
// Single-flight: a dashboard fires a dozen parallel requests, and all of them
// would fail at once on the same expired token. Only the first triggers a
// refresh; the others wait for its outcome instead of racing to spend the
// (rotating, single-use) refresh token.
let refreshInFlight = null;
let refreshSansReseau = false;

const refreshAccessToken = async () => {
  const refresh = getRefreshToken();
  if (!refresh) return null;
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(`${API_BASE}/auth/refresh`, { refresh_token: refresh })
      .then((response) => {
        const data = response.data?.data || response.data || {};
        if (!data.token) return null;
        setAuthToken(data.token);
        // The server rotates the refresh token: keeping the old one would fail
        // on the next attempt and log the user out anyway.
        if (data.refresh_token) setRefreshToken(data.refresh_token);
        return data.token;
      })
      .catch((erreur) => {
        // Un renouvellement qui n'a pas atteint le serveur n'est pas un refus:
        // la session est gardée, elle sera renouvelée au retour du réseau.
        refreshSansReseau = !erreur?.response;
        return null;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};

// ---------------------------------------------------------------------------
// Online status, server reachability and sync engine
// ---------------------------------------------------------------------------

// The mobile app uses the same batch size; a single unbounded POST after a long
// outage means several MB that time out and leave the queue untouched forever.
const SYNC_BATCH_SIZE = 50;
const SYNC_REQUEST_TIMEOUT = 20000;

// Reachability probe. navigator.onLine only tells us an interface is up: with a
// captive portal or a degraded 2G link it stays true while the backend is
// unreachable — the most common field situation, and the one where the queue
// used to never restart on its own.
const HEALTH_URL = BACKEND_URL
  ? `${BACKEND_URL}/api/health/live`
  : '/api/health/live';
const HEALTH_TIMEOUT = 6000;
const HEALTH_INTERVAL = 20000;

// Exponential backoff between two failed sync attempts, capped at 5 minutes.
const RETRY_BASE_DELAY = 5000;
const RETRY_MAX_DELAY = 5 * 60 * 1000;

let isOnline = navigator.onLine;
let isDemoMode = false;
const onlineListeners = new Set();
const pendingChangeListeners = new Set();

let syncInFlight = null;
let retryTimer = null;
let retryAttempt = 0;

export const getOnlineStatus = () => isOnline && !isDemoMode;

export const setDemoMode = (value) => {
  isDemoMode = Boolean(value);
  if (isDemoMode) {
    isOnline = false;
    cancelSyncRetry();
  }
};

export const getDemoMode = () => isDemoMode;

export const addOnlineListener = (callback) => {
  onlineListeners.add(callback);
  return () => onlineListeners.delete(callback);
};

// Lets the UI refresh its pending counter immediately instead of waiting for the
// next 10s poll after an offline write.
export const addPendingChangeListener = (callback) => {
  pendingChangeListeners.add(callback);
  return () => pendingChangeListeners.delete(callback);
};

const notifyPendingChange = () => {
  pendingChangeListeners.forEach((cb) => {
    try {
      cb();
    } catch (err) {
      console.warn('Pending change listener failed:', err);
    }
  });
};

const setOnline = (value) => {
  const next = Boolean(value) && !isDemoMode;
  if (next === isOnline) return;
  isOnline = next;
  onlineListeners.forEach((cb) => cb(next));
  if (next) {
    cancelSyncRetry();
    syncPendingChanges();
  }
};

// Deliberately uses bare axios, not the `api` instance: no auth header, no
// response interceptor, so a failing probe never triggers a toast or a logout.
const pingServer = async () => {
  if (isDemoMode || !navigator.onLine) return false;
  try {
    await axios.get(HEALTH_URL, {
      timeout: HEALTH_TIMEOUT,
      headers: { 'Cache-Control': 'no-cache' },
    });
    return true;
  } catch (err) {
    return false;
  }
};

// A single failed probe is not proof of an outage (a request aborted by a page
// transition looks exactly like one). Recovery is trusted immediately, loss only
// after two consecutive failures, which keeps the badge from flapping.
let failedProbes = 0;

export const checkConnectivity = async () => {
  const reachable = await pingServer();
  if (reachable) {
    failedProbes = 0;
    setOnline(true);
  } else {
    failedProbes += 1;
    if (failedProbes >= 2 || !navigator.onLine) setOnline(false);
  }
  return reachable;
};

const cancelSyncRetry = () => {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  retryAttempt = 0;
};

const scheduleSyncRetry = () => {
  if (retryTimer || isDemoMode) return;
  const backoff = Math.min(
    RETRY_BASE_DELAY * 2 ** retryAttempt,
    RETRY_MAX_DELAY,
  );
  // ±30% jitter so a whole fleet coming back on the same relay does not retry in lockstep.
  const delay = Math.round(backoff * (0.7 + Math.random() * 0.6));
  retryAttempt += 1;

  retryTimer = setTimeout(async () => {
    retryTimer = null;
    if (await checkConnectivity()) {
      const result = await syncPendingChanges();
      if (result?.success) cancelSyncRetry();
    } else {
      scheduleSyncRetry();
    }
  }, delay);
};

window.addEventListener('online', () => {
  if (isDemoMode) return;
  // The interface is back, but that says nothing about the server: confirm first.
  checkConnectivity();
});

window.addEventListener('offline', () => {
  setOnline(false);
});

// Les écrans appellent toujours l'API: sans réseau, la passerelle locale répond
// (lectures) et met en file (écritures) sans que l'écran ait à le savoir.
const canCallApi = () => !isDemoMode;

const passerelle = installerPasserelle(api, {
  enLigne: () => isOnline && !isDemoMode,
  desactivee: () => isDemoMode,
  sonder: () => {
    checkConnectivity();
  },
  mettreEnFile: (donnees) => syncQueue.add(TYPE_REQUETE_EN_FILE, 'request', donnees),
  localPret: donneesLocalesSontPretes,
});
// Un appareil déjà téléchargé lit en local dès l'ouverture; un appareil neuf, après son premier téléchargement.
Promise.resolve(appState?.get?.('telechargementInitial'))
  .then((fait) => definirDonneesLocalesPretes(Boolean(fait?.at)))
  .catch(() => {});
export const attendreModeleLocal = () => passerelle.attendre();

fullSync?.definirCrochetDePage?.(fusionnerPageDuPull);

// Background probe: detects both silent losses (portal, dead backend) and
// recoveries that never emit an `online` event.
setInterval(() => {
  if (isDemoMode) return;
  if (!navigator.onLine) {
    setOnline(false);
    return;
  }
  checkConnectivity();
}, HEALTH_INTERVAL);

// Transport-level failures (timeout, DNS, connection reset) must NOT count towards
// abandoning an item: a three-day outage is not the item's fault. Only a server
// that answers and keeps refusing does.
const isReproducibleRefusal = (error) => {
  const status = error?.response?.status;
  if (!status) return false;
  return status < 500 && ![401, 408, 429].includes(status);
};

const conflictReason = (conflict) =>
  conflict?.message || conflict?.type || 'Refusée';

// Phrase française d'une erreur (jamais `error.message` d'axios, qui dit
// « Request failed with status code 422 »). Voir `lib/erreurs.js`.
export const apiErrorMessage = (error, fallback) => messageErreur(error, fallback);

export { messageErreur };

// Sync pending changes to server
export const syncPendingChanges = async () => {
  if (isDemoMode) return { success: true, synced: 0, demo: true };
  if (syncInFlight) return syncInFlight;

  if (!isOnline || !getAuthToken())
    return { success: false, reason: 'offline' };

  syncInFlight = (async () => {
    let synced = 0;
    const allConflicts = [];
    // Guards against re-processing the same items forever within a single run.
    const seen = new Set();

    try {
      for (;;) {
        const toutes = await syncQueue.getPending(SYNC_BATCH_SIZE);
        if (toutes.length === 0) break;
        if (toutes.every((item) => seen.has(item.id))) break;

        // Les écritures REST mises en file sont rejouées une à une, dans l'ordre;
        // les anciens éléments (protocole /sync) partent par lots, comme avant.
        if (toutes[0].entity_type === TYPE_REQUETE_EN_FILE) {
          seen.add(toutes[0].id);
          const issue = await rejouerRequete(toutes[0], { api, syncQueue });
          if (issue === 'ok') synced += 1;
          notifyPendingChange();
          continue;
        }
        const indexHttp = toutes.findIndex((item) => item.entity_type === TYPE_REQUETE_EN_FILE);
        const pending = indexHttp === -1 ? toutes : toutes.slice(0, indexHttp);
        pending.forEach((item) => seen.add(item.id));

        const items = pending.map((item) => ({
          id: String(item.id),
          // The server keys replay protection on this, not on `id`: `id` is
          // Dexie's auto-increment, which is only unique inside one device's
          // queue. Items queued before this field existed have none, and the
          // server falls back to a payload fingerprint for those.
          client_id: item.client_id,
          entity_type: item.entity_type,
          action: item.action,
          data: item.data,
          client_timestamp: item.client_timestamp,
          synced: false,
        }));

        let response;
        try {
          response = await api.post(
            '/sync',
            { items },
            { timeout: SYNC_REQUEST_TIMEOUT, skipToast: true },
          );
        } catch (error) {
          if (isReproducibleRefusal(error)) {
            await syncQueue.markAttemptFailed(
              pending.map((item) => item.id),
              {
                reason: `HTTP ${error.response.status}`,
              },
            );
          }
          throw error;
        }

        const syncedIds = (response.data?.synced_ids || [])
          .map((id) => parseInt(id, 10))
          .filter(Number.isFinite);
        if (syncedIds.length > 0) {
          await syncQueue.markSynced(syncedIds);
          await syncQueue.clearSynced();
          synced += syncedIds.length;
        }

        const conflicts = response.data?.conflicts || [];
        allConflicts.push(...conflicts);

        // A conflict is a definitive refusal (stale data, missing parent…). Re-sending
        // it changes nothing: park it so it stops poisoning every future batch.
        const conflictIds = [];
        for (const conflict of conflicts) {
          const id = parseInt(conflict?.sync_item_id, 10);
          if (!Number.isFinite(id)) continue;
          conflictIds.push(id);
          await syncQueue.markAttemptFailed([id], {
            reason: conflictReason(conflict),
            definitive: true,
          });
        }

        // Items the server neither acknowledged nor flagged. Count an attempt so they
        // cannot be resent indefinitely.
        const handled = new Set([...syncedIds, ...conflictIds]);
        const ignored = pending
          .map((item) => item.id)
          .filter((id) => !handled.has(id));
        if (ignored.length > 0) {
          await syncQueue.markAttemptFailed(ignored, {
            reason: 'Non traitée',
          });
        }

        notifyPendingChange();
      }

      cancelSyncRetry();
      return { success: true, synced, conflicts: allConflicts };
    } catch (error) {
      console.error('Sync failed:', error);
      notifyPendingChange();
      // Re-probe: if the server became unreachable, flip the UI to offline instead of
      // pretending everything is fine, and keep retrying with backoff. A failed sync
      // plus a failed probe is evidence enough — no need for a second probe here.
      pingServer().then((reachable) => {
        failedProbes = reachable ? 0 : failedProbes + 1;
        setOnline(reachable);
      });
      scheduleSyncRetry();
      return { success: false, synced, conflicts: allConflicts, error };
    } finally {
      syncInFlight = null;
    }
  })();

  return syncInFlight;
};

// Queue helpers re-exported so the UI does not need to import db.js internals.
export const getPendingCount = () => syncQueue.countPending();
export const getFailedQueueItems = () => syncQueue.getFailed();
export const discardQueueItem = async (id) => {
  const item = await syncQueue.get(id);
  const removed = await syncQueue.remove(id);
  if (removed && item) await liberer(item, api).catch(() => {});
  if (removed) notifyPendingChange();
  return removed;
};
export const retryQueueItem = async (id) => {
  const retried = await syncQueue.retry(id);
  if (retried) notifyPendingChange();
  return retried;
};

// Full data sync from server
const TAILLE_PAGE_DU_PULL = 400;
export const performFullSync = async (since = null) => {
  if ((!isOnline && !isDemoMode) || !getAuthToken())
    return { success: false, reason: 'offline' };

  try {
    const data = await pullAllPages({
      since,
      getPage: async ({ since: cursor, offset }) => {
        // Pages modestes, après les requêtes de l'écran: le fil principal n'est jamais saturé.
        await attendreEcranLibre();
        const params = { offset, limit: TAILLE_PAGE_DU_PULL };
        if (cursor) params.since = cursor;
        return (await api.get('/sync/full', { params, arrierePlan: true })).data;
      },
      savePage: async (page) => {
        const resultat = await fullSync.saveAll(page);
        await ceder();
        return resultat;
      },
      commitCursor: (timestamp) => fullSync.commitCursor(timestamp),
    });
    // Les lignes imitées sur l'appareil cèdent la place à celles du serveur.
    await purgerDerives().catch(() => {});
    await noterSynchroLocale().catch(() => {});
    return { success: true, data };
  } catch (error) {
    console.error('Full sync failed:', error);
    return { success: false, error };
  }
};

// --- Données sur l'appareil -------------------------------------------------------------
// Téléchargement complet après la connexion, puis mises à jour incrémentales
// (avec les suppressions). Silencieux: aucune erreur n'est montrée à l'opérateur.
const DUREE_TELECHARGEMENT_VALIDE = 12 * 60 * 60 * 1000;
const DELAI_RAFRAICHISSEMENT_LISTES = 10 * 60 * 1000;
const DELAI_RECONCILIATION = 30 * 60 * 1000;
let miseAJourEnCours = null;

const reconcilier = () =>
  reconcilierSuppressions(async ({ offset }) =>
    (await api.get('/sync/ids', { params: { offset }, skipToast: true, arrierePlan: true })).data,
  ).catch(() => {});

export const miseAJourLocale = ({ force = false } = {}) => {
  if (isDemoMode || !isOnline || !getAuthToken()) return Promise.resolve(false);
  if (miseAJourEnCours) return miseAJourEnCours;
  miseAJourEnCours = (async () => {
    try {
      // Les écritures en attente partent d'abord: le pull ne doit pas revenir en arrière.
      const envoi = await syncPendingChanges();
      const fait = await appState.get('telechargementInitial');
      const recent = fait?.at && Date.now() - Date.parse(fait.at) < DUREE_TELECHARGEMENT_VALIDE;
      if (!recent || force) {
        const complet = await performFullSync(null);
        if (!complet.success) return false;
        await reconcilier();
        await telechargerListes(api);
        await telechargerUniques(api);
        await telechargerDetails(api);
        await appState.set('telechargementInitial', { at: new Date().toISOString(), listes: Date.now() });
        definirDonneesLocalesPretes(true);
        return true;
      }
      const dernier = await fullSync.getLastSyncTime();
      const incremental = await performFullSync(dernier);
      if (!incremental.success) return false;
      // Des envois viennent d'aboutir: les agrégats calculés par le serveur
      // (avancement, restes, stock) sont relus tout de suite.
      if (envoi?.synced > 0) fait.listes = 0;
      if (!fait.listes || Date.now() - fait.listes > DELAI_RAFRAICHISSEMENT_LISTES) {
        await telechargerListes(api);
        await telechargerUniques(api);
        fait.listes = Date.now();
      }
      // Ce qui a été supprimé en dur ailleurs disparaît aussi de l'appareil.
      if (!fait.ids || Date.now() - fait.ids > DELAI_RECONCILIATION) {
        await reconcilier();
        fait.ids = Date.now();
      }
      await appState.set('telechargementInitial', fait);
      return true;
    } catch (erreur) {
      console.warn('Local update failed:', erreur);
      return false;
    } finally {
      miseAJourEnCours = null;
    }
  })();
  return miseAJourEnCours;
};

// Auth API — le web s'authentifie par email + mot de passe uniquement.
// Le login par code admin (POST /auth/login) a été supprimé côté backend ;
// les codes d'accès terrain restent réservés à l'application mobile.
export const authAPI = {
  async loginWithEmail(email, password) {
    const response = await api.post('/users/login', { email, password });
    return response.data?.data || response.data;
  },

  async verify() {
    try {
      const response = await api.get('/auth/verify');
      return response.data;
    } catch (error) {
      return { valid: false };
    }
  },
};

// Organizations API
export const organizationsAPI = {
  async getAll(params = {}) {
    const response = await api.get('/organizations', { params });
    return response.data?.data || response.data;
  },

  // Toutes les organisations, pour les menus : l'API pagine par skip/limit
  // et s'arrête à 100 par défaut.
  async getEvery() {
    const { items } = await fetchAllPages(({ page, page_size: size }) =>
      organizationsAPI.getAll({ skip: (page - 1) * size, limit: size }),
    );
    return items;
  },

  async getById(id) {
    const response = await api.get(`/organizations/${id}`);
    return response.data?.data || response.data;
  },

  async create(data) {
    const response = await api.post('/organizations', data);
    return response.data?.data || response.data;
  },

  async update(id, data) {
    const response = await api.patch(`/organizations/${id}`, data);
    return response.data?.data || response.data;
  },

  async delete(id) {
    const response = await api.delete(`/organizations/${id}`);
    return response.data?.data || response.data;
  },
};

// Profils d'accès : modules, montants visibles et mode de connexion, créés par
// l'administrateur de la société (le super admin passe `organization_id`).
export const accessProfilesAPI = {
  async list(organizationId) {
    const params = organizationId ? { organization_id: organizationId } : {};
    const response = await api.get('/access-profiles', { params });
    return response.data?.data || [];
  },

  async create(data, organizationId) {
    const params = organizationId ? { organization_id: organizationId } : {};
    const response = await api.post('/access-profiles', data, { params });
    return response.data?.data || response.data;
  },

  async update(id, data, organizationId) {
    const params = organizationId ? { organization_id: organizationId } : {};
    const response = await api.patch(`/access-profiles/${id}`, data, { params });
    return response.data?.data || response.data;
  },

  async remove(id, organizationId) {
    const params = organizationId ? { organization_id: organizationId } : {};
    const response = await api.delete(`/access-profiles/${id}`, { params });
    return response.data?.data || response.data;
  },
};

// Users API
export const usersAPI = {
  async getAll(params = {}) {
    const response = await api.get('/users', { params });
    return response.data?.data || response.data;
  },

  async getById(id) {
    const response = await api.get(`/users/${id}`);
    return response.data?.data || response.data;
  },

  async create(data) {
    const response = await api.post('/users', data);
    return response.data?.data || response.data;
  },

  async update(id, data) {
    const response = await api.patch(`/users/${id}`, data);
    return response.data?.data || response.data;
  },

  async delete(id) {
    const response = await api.delete(`/users/${id}`);
    return response.data?.data || response.data;
  },

  async regenerateAccessCode(id) {
    const response = await api.post(`/users/${id}/regenerate-code`);
    return response.data?.data || response.data;
  },

  async suspend(id, reason) {
    const response = await api.post(`/users/${id}/suspend`, { reason });
    return response.data?.data || response.data;
  },

  async reactivate(id) {
    const response = await api.post(`/users/${id}/reactivate`);
    return response.data?.data || response.data;
  },

  async getMyPreferences() {
    const response = await api.get('/users/me/preferences');
    return response.data?.data || response.data;
  },

  async updateMyPreferences(preferences) {
    const response = await api.patch('/users/me/preferences', preferences);
    return response.data?.data || response.data;
  },

  async getMe() {
    const response = await api.get('/users/me/profile');
    return response.data?.data || response.data;
  },

  async updateMy(data) {
    const response = await api.patch('/users/me', data);
    return response.data?.data || response.data;
  },

  async changeMyPassword(currentPassword, newPassword) {
    const response = await api.post('/users/me/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data?.data || response.data;
  },

  async regenerateMyAccessCode() {
    const response = await api.post('/users/me/regenerate-access-code');
    return response.data?.data || response.data;
  },
};

// Licenses API (Super Admin only)
export const licensesAPI = {
  async getAll(params = {}) {
    const response = await api.get('/licenses', { params });
    return response.data?.data || response.data;
  },

  // Toutes les licences : l'API pagine par skip/limit (100 par défaut, sans plafond).
  async getEvery(params = {}) {
    const { items } = await fetchAllPages(({ page, page_size: size }) =>
      licensesAPI.getAll({ ...params, skip: (page - 1) * size, limit: size }),
    );
    return items;
  },
  async getById(id) {
    const response = await api.get(`/licenses/${id}`);
    return response.data?.data || response.data;
  },
  async create(data) {
    const response = await api.post('/licenses', data);
    return response.data?.data || response.data;
  },
  async update(id, data) {
    const response = await api.patch(`/licenses/${id}`, data);
    return response.data?.data || response.data;
  },
  async suspend(id, reason) {
    const response = await api.post(`/licenses/${id}/suspend`, { reason });
    return response.data?.data || response.data;
  },
  async reactivate(id) {
    const response = await api.post(`/licenses/${id}/reactivate`);
    return response.data?.data || response.data;
  },
  async revoke(id) {
    const response = await api.post(`/licenses/${id}/revoke`);
    return response.data?.data || response.data;
  },
  async renew(id, payload) {
    const response = await api.post(`/licenses/${id}/renew`, payload);
    return response.data?.data || response.data;
  },
  async getAudit(id, params = {}) {
    const response = await api.get(`/licenses/${id}/audit`, { params });
    return response.data?.data || response.data;
  },
  async getActiveForOrg(orgId) {
    const response = await api.get(`/licenses/org/${orgId}/active`);
    return response.data?.data || response.data;
  },
  async getMRR() {
    const response = await api.get('/licenses/stats/mrr');
    return response.data?.data || response.data;
  },
  async getExpiringSoon(days = 30) {
    const response = await api.get('/licenses/stats/expiring-soon', { params: { days } });
    return response.data?.data || response.data;
  },
  async downloadInvoice(id, periodMonths = 12) {
    const response = await api.get(`/licenses/${id}/invoice`, {
      params: { period_months: periodMonths },
      responseType: 'blob',
    });
    return response.data;
  },
};

// Admin API (Super Admin only)
export const adminAPI = {
  async getStatsOverview() {
    const response = await api.get('/admin/stats/overview');
    return response.data?.data || response.data;
  },
  async getStatsTimeline(days = 30) {
    const response = await api.get('/admin/stats/timeline', { params: { days } });
    return response.data?.data || response.data;
  },
  async suspendOrganization(id) {
    const response = await api.post(`/admin/organizations/${id}/suspend`);
    return response.data?.data || response.data;
  },
  async reactivateOrganization(id) {
    const response = await api.post(`/admin/organizations/${id}/reactivate`);
    return response.data?.data || response.data;
  },
  async listUsers(params = {}) {
    const response = await api.get('/admin/users', { params });
    return response.data?.data || response.data;
  },

  // Tous les utilisateurs de la plateforme : pagination skip/limit côté API.
  async listEveryUser(params = {}) {
    const { items } = await fetchAllPages(({ page, page_size: size }) =>
      adminAPI.listUsers({ ...params, skip: (page - 1) * size, limit: size }),
    );
    return { items, total: items.length };
  },
  async getAuditLogs(params = {}) {
    const response = await api.get('/admin/audit-logs', { params });
    return response.data?.data || response.data;
  },
  // Forensic audit
  async getCriticalLogs(limit = 20) {
    const response = await api.get('/admin/audit/critical', { params: { limit } });
    return response.data?.data || response.data;
  },
  async getAnomalies(hours = 24) {
    const response = await api.get('/admin/audit/anomalies', { params: { hours } });
    return response.data?.data || response.data;
  },
  async getUserTimeline(userId, hours = 24) {
    const response = await api.get(`/admin/audit/user-timeline/${userId}`, { params: { hours } });
    return response.data?.data || response.data;
  },
  async getAuditAnalytics(days = 30) {
    const response = await api.get('/admin/audit/analytics', { params: { days } });
    return response.data?.data || response.data;
  },
  // Org summary
  async getOrganizationSummary(orgId) {
    const response = await api.get(`/admin/organizations/${orgId}/summary`);
    return response.data?.data || response.data;
  },
  // Une liste de la fiche société, lue par pages: section = users | trucks | trips | audit.
  // Rend { items, total, skip, limit }; `total` compte toute la liste.
  async getOrganizationList(orgId, section, params = {}) {
    const response = await api.get(`/admin/organizations/${orgId}/${section}`, { params });
    return response.data?.data || response.data;
  },
  // Une ligne par société : utilisateurs, camions, activité, licence.
  async getOrganizationsOverview() {
    const response = await api.get('/admin/organizations/overview');
    return response.data?.data || response.data;
  },
};

// Sociétés de démonstration (super administrateur) : photographier, remettre à neuf.
export const demoAdminAPI = {
  async getSnapshot(orgId) {
    const response = await api.get(`/demo/organizations/${orgId}/snapshot`, { skipToast: true });
    return response.data?.data || response.data;
  },
  async capture(orgId, label) {
    const response = await api.post(`/demo/organizations/${orgId}/snapshot`, null, {
      params: label ? { label } : undefined,
    });
    return response.data?.data || response.data;
  },
  async restore(orgId) {
    const response = await api.post(`/demo/organizations/${orgId}/restore`);
    return response.data?.data || response.data;
  },
};

// Backend Ops API (Super Admin only)
export const backendOpsAPI = {
  async getHealth() {
    const response = await api.get('/backend-ops/health');
    return response.data?.data || response.data;
  },
  async listBackups() {
    const response = await api.get('/backend-ops/backups');
    return response.data?.data || response.data;
  },
  async createBackup() {
    const response = await api.post('/backend-ops/backups');
    return response.data?.data || response.data;
  },
  async deleteBackup(name) {
    const response = await api.delete(`/backend-ops/backups/${encodeURIComponent(name)}`);
    return response.data?.data || response.data;
  },
  async restoreBackup(name) {
    const response = await api.post(`/backend-ops/backups/${encodeURIComponent(name)}/restore`, null, {
      params: { confirm: true },
    });
    return response.data?.data || response.data;
  },
  async purgeSoftDeleted(days = 30, dryRun = true) {
    const response = await api.post('/backend-ops/purge/soft-deleted', null, {
      params: { days, dry_run: dryRun },
    });
    return response.data?.data || response.data;
  },
  async purgeGps(days = 90, dryRun = true) {
    const response = await api.post('/backend-ops/purge/gps', null, {
      params: { days, dry_run: dryRun },
    });
    return response.data?.data || response.data;
  },
  async reindex(collection) {
    const response = await api.post(`/backend-ops/reindex/${collection}`);
    return response.data?.data || response.data;
  },
  async collectionsStats() {
    const response = await api.get('/backend-ops/collections/stats');
    return response.data?.data || response.data;
  },
  async cacheStats() {
    const response = await api.get('/backend-ops/cache/stats');
    return response.data?.data || response.data;
  },
  async cacheFlush() {
    const response = await api.post('/backend-ops/cache/flush');
    return response.data?.data || response.data;
  },
  async wsStats() {
    const response = await api.get('/backend-ops/websocket/stats');
    return response.data?.data || response.data;
  },
  async wsFlush() {
    const response = await api.post('/backend-ops/websocket/flush');
    return response.data?.data || response.data;
  },
  async listMigrations() {
    const response = await api.get('/backend-ops/migrations');
    return response.data?.data || response.data;
  },
  async runMigration(script) {
    const response = await api.post(`/backend-ops/migrations/${script}`);
    return response.data?.data || response.data;
  },
  async regenerateExpiredCodes(days = 180) {
    const response = await api.post('/backend-ops/access-codes/regenerate-expired', null, {
      params: { days },
    });
    return response.data?.data || response.data;
  },
  async logsTail(lines = 100) {
    const response = await api.get('/backend-ops/logs/tail', { params: { lines } });
    return response.data?.data || response.data;
  },
  async exportCollection(collection, format = 'json', dateFrom, dateTo) {
    const response = await api.get(`/backend-ops/export/${collection}`, {
      params: { format, date_from: dateFrom, date_to: dateTo },
      responseType: 'blob',
    });
    return response.data;
  },
};

// Tracking API (fleet live + batch)
export const trackingAPI = {
  async getFleetLive(params = {}) {
    const response = await api.get('/tracking/fleet/live', { params });
    return response.data?.data || response.data;
  },
  async submitBatch(payload) {
    const response = await api.post('/tracking/batch', payload);
    return response.data?.data || response.data;
  },
  async getTruckLastPosition(truckId) {
    const response = await api.get(`/tracking/trucks/${truckId}/last-position`);
    return response.data?.data ?? null;
  },
  async getHistory(tripId, params = {}) {
    const response = await api.get(`/tracking/${tripId}/history`, { params });
    return response.data?.data || response.data;
  },
  async getLatest(tripId) {
    const response = await api.get(`/tracking/${tripId}/latest`);
    return response.data?.data || response.data;
  },
};

export const orgSettingsAPI = {
  async getBlockingPolicy(organizationId) {
    const response = await api.get('/org-settings/blocking-policy', {
      params: organizationId ? { organization_id: organizationId } : undefined,
    });
    return response.data?.data || response.data;
  },
  // Un champ absent reste inchangé; `null` réinitialise au défaut produit.
  async updateBlockingPolicy(patch, organizationId) {
    const response = await api.put('/org-settings/blocking-policy', patch, {
      params: organizationId ? { organization_id: organizationId } : undefined,
    });
    return response.data?.data || response.data;
  },
  async getWorkflowGates(organizationId) {
    const response = await api.get('/org-settings/workflow-gates', {
      params: organizationId ? { organization_id: organizationId } : undefined,
    });
    return response.data?.data || response.data;
  },
  async updateWorkflowGates(patch, organizationId) {
    const response = await api.put('/org-settings/workflow-gates', patch, {
      params: organizationId ? { organization_id: organizationId } : undefined,
    });
    return response.data?.data || response.data;
  },
};

export const gpsProvidersAPI = {
  async getAll() {
    const response = await api.get('/tracking/providers');
    return response.data?.data || response.data;
  },
  async create(data) {
    const response = await api.post('/tracking/providers', data);
    return response.data?.data || response.data;
  },
  async update(id, data) {
    const response = await api.patch(`/tracking/providers/${id}`, data);
    return response.data?.data || response.data;
  },
  async delete(id) {
    await api.delete(`/tracking/providers/${id}`);
    return true;
  },
  // Nom canonique côté backend : la route ne contacte aucun boîtier, elle rend
  // l'état constaté (configuration + positions déjà reçues).
  async diagnostics(id) {
    const response = await api.post(`/tracking/providers/${id}/diagnostics`);
    return response.data?.data || response.data;
  },
  /**
   * @deprecated Alias historique de `diagnostics()`. `POST
   * /tracking/providers/{id}/test` reste servi par le backend mais y est marqué
   * `deprecated` : les nouveaux appels doivent viser `/diagnostics`.
   */
  async test(id) {
    return gpsProvidersAPI.diagnostics(id);
  },
};

export const openwaAPI = {
  async health() {
    const response = await api.get('/integrations/openwa/health');
    return response.data?.data || response.data;
  },
  async outbox(limit = 30) {
    const response = await api.get('/integrations/openwa/outbox', { params: { limit } });
    return response.data?.data || response.data;
  },
  /** Une page d'envois, du plus récent au plus ancien: `{ items, total }` (en-tête `X-Total-Count`). */
  async outboxPage({ page = 1, limit = 20 } = {}) {
    const response = await api.get('/integrations/openwa/outbox', { params: { page, limit } });
    const items = response.data?.data || response.data || [];
    return { items, total: getTotalFromHeaders(response) ?? items.length };
  },
  async sendTest(data = {}) {
    const response = await api.post('/integrations/openwa/test', data);
    return response.data?.data || response.data;
  },
  async retry(id) {
    const response = await api.post(`/integrations/openwa/outbox/${id}/retry`);
    return response.data?.data || response.data;
  },
};

// Assistant de la plateforme (spec 043): la discussion du bot WhatsApp dans
// l'application. Rien n'est gardé côté serveur; une saisie n'est écrite qu'à
// `confirmDraft`, avec la clé d'idempotence du brouillon.
export const assistantAPI = {
  async status() {
    const response = await api.get('/assistant/status');
    return response.data?.data || response.data;
  },
  async message(message, history = []) {
    const response = await api.post('/assistant/message', { message, history });
    return response.data?.data || response.data;
  },
  async searchTrucks(q) {
    const response = await api.get('/assistant/trucks', { params: { q } });
    return response.data?.data || response.data || [];
  },
  async truckTrips(truckId) {
    const response = await api.get(`/assistant/trucks/${encodeURIComponent(truckId)}/trips`);
    return response.data?.data || response.data || [];
  },
  async confirmDraft(flow, body) {
    const response = await api.post(`/assistant/drafts/${encodeURIComponent(flow)}`, body);
    return response.data?.data || response.data;
  },
};

// Bot WhatsApp des opérateurs: `me*` concerne le compte connecté (statut,
// code de liaison, déliaison), `access` est l'écran de l'ADMIN_SOCIETE qui
// accorde ou retire l'accès bot aux membres éligibles de son organisation.
export const whatsappBotAPI = {
  async me() {
    const response = await api.get('/integrations/whatsapp-bot/me');
    return response.data?.data || response.data;
  },
  async requestLinkCode(phone) {
    const response = await api.post('/integrations/whatsapp-bot/me/link-code', { phone });
    return response.data?.data || response.data;
  },
  async unlink() {
    const response = await api.delete('/integrations/whatsapp-bot/me/link');
    return response.data?.data || response.data;
  },
  async setBrief(enabled, hour) {
    // `hour` omis (undefined): le serveur garde l'heure déjà réglée.
    const response = await api.put('/integrations/whatsapp-bot/me/brief', { enabled, hour });
    return response.data?.data || response.data;
  },
  async setDemoEnabled(enabled) {
    const response = await api.put('/integrations/whatsapp-bot/demo', { enabled });
    return response.data?.data || response.data;
  },
  async setAiEnabled(enabled) {
    // Assistant IA du bot (spec 042), pour toute la société: ADMIN_SOCIETE seulement.
    const response = await api.put('/integrations/whatsapp-bot/ai', { enabled });
    return response.data?.data || response.data;
  },
  async listAccess() {
    const response = await api.get('/integrations/whatsapp-bot/access');
    return response.data?.data || response.data;
  },
  async setAccess(userId, enabled) {
    const response = await api.put(`/integrations/whatsapp-bot/access/${userId}`, { enabled });
    return response.data?.data || response.data;
  },
};

export const geoLocationsAPI = {
  async getAll(params = {}) {
    const response = await api.get('/geo-locations', { params });
    return response.data?.data || response.data;
  },
  async getEvents(params = {}) {
    const response = await api.get('/geo-locations/events', { params });
    return response.data?.data || response.data;
  },
  async create(data) {
    const response = await api.post('/geo-locations', data);
    return response.data?.data || response.data;
  },
  async update(id, data) {
    const response = await api.patch(`/geo-locations/${id}`, data);
    return response.data?.data || response.data;
  },
  async activate(id) {
    const response = await api.post(`/geo-locations/${id}/activate`);
    return response.data?.data || response.data;
  },
  async deactivate(id) {
    const response = await api.post(`/geo-locations/${id}/deactivate`);
    return response.data?.data || response.data;
  },
  async delete(id) {
    await api.delete(`/geo-locations/${id}`);
    return true;
  },
};

export const corridorsAPI = {
  /**
   * Les corridors d'importation visibles: ceux de l'organisation et ceux de
   * la plateforme. Référentiel en lecture seule — leurs coordonnées ont été
   * vérifiées poste par poste et ne s'éditent pas depuis un écran.
   */
  async getAll(params = {}) {
    const response = await api.get('/corridors/', { params });
    return response.data?.data || response.data || [];
  },
  async getRoute(corridorId) {
    const response = await api.get(`/corridors/${corridorId}/route`);
    return response.data?.data || response.data;
  },
};

export const documentsAPI = {
  async getAll(params = {}) {
    // `entity_type` + `entity_id` ne gardent que les pièces rattachées à une
    // entité (les papiers d'un camion, par exemple).
    const response = await api.get('/documents', { params });
    const rows = response.data?.data || response.data;
    return Array.isArray(rows) ? rows.map(normalizeDocument) : [];
  },
  /**
   * Dépose un fichier et renvoie le document archivé.
   *
   * Le web n'avait pas d'envoi de document: seule l'application chauffeur
   * savait le faire. Un état des lieux photographié depuis le bureau — au
   * retour d'un camion loué, typiquement — n'avait donc aucun moyen d'être
   * joint à sa pièce.
   */
  async upload(file, docType = 'OTHER', metadata = {}) {
    // Rattachement optionnel: metadata.entity_type ('truck') + metadata.entity_id.
    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', docType);
    Object.entries(metadata).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        formData.append(key, String(value));
      }
    });
    const response = await api.post('/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return normalizeDocument(response.data?.data || response.data);
  },
  async retryOcr(id) {
    const response = await api.post(`/documents/${documentId(id)}/ocr/retry`);
    return normalizeDocument(response.data?.data || response.data);
  },
  /**
   * Retire un document des listes sans supprimer le fichier. `motif` (facultatif)
   * est conservé avec la date et l'auteur; sans lui, le serveur note
   * « Archivage utilisateur ».
   */
  async archive(id, motif) {
    const raison = typeof motif === 'string' ? motif.trim() : '';
    const response = await api.post(
      `/documents/${documentId(id)}/archive`,
      raison ? { reason: raison } : undefined,
    );
    return normalizeDocument(response.data?.data || response.data);
  },
  /** Remet un document archivé dans les listes, sur son trajet et sa fiche. */
  async unarchive(id) {
    const response = await api.post(`/documents/${documentId(id)}/unarchive`);
    return normalizeDocument(response.data?.data || response.data);
  },
  /**
   * Les documents archivés, paginés: `{ items, total }`. Chaque ligne porte
   * `archived_at`, `archived_by` et `archive_reason`. `params`: `page`,
   * `page_size`, `q` (recherche), `entity_type` / `entity_id`.
   */
  async listArchived(params = {}) {
    const response = await api.get('/documents/archived', {
      params: { ...params, include_total: true },
    });
    const rows = response.data?.data || response.data;
    const items = Array.isArray(rows) ? rows.map(normalizeDocument) : [];
    const total = getTotalFromHeaders(response);
    return { items, total: total ?? items.length };
  },
};

export const missionIntegrityAPI = {
  async getAnomalies(params = {}) {
    const response = await api.get('/mission-integrity/anomalies', { params });
    return response.data?.data || response.data;
  },
  /**
   * Une page d'anomalies (`skip`/`limit`): les lignes, le total du relevé et
   * ses chiffres entiers (`summary`), que la page seule ne permet pas de calculer.
   */
  async getAnomaliesPage(params = {}) {
    const response = await api.get('/mission-integrity/anomalies', { params });
    const items = response.data?.data || [];
    const meta = response.data?.meta || {};
    return {
      items,
      total: getTotalFromHeaders(response) ?? meta.total ?? items.length,
      summary: meta.summary || null,
    };
  },
  async recomputeTrip(tripId) {
    const response = await api.post(`/mission-integrity/trips/${tripId}/recompute`);
    return response.data?.data || response.data;
  },
  async justify(scoreId, comment) {
    const response = await api.post(`/mission-integrity/${scoreId}/justify`, { comment });
    return response.data?.data || response.data;
  },
};

export const proofOfDeliveryAPI = {
  async getPack(tripId) {
    const response = await api.get(`/proof-of-delivery/trips/${tripId}/proof-pack`);
    return response.data?.data || response.data;
  },
  async validateDocument(tripId, documentId, forceBillingReady = false) {
    const response = await api.post(`/proof-of-delivery/trips/${tripId}/validate-document`, {
      document_id: documentId,
      force_billing_ready: forceBillingReady,
    });
    return response.data?.data || response.data;
  },
  async markBillingReady(tripId, comment) {
    const response = await api.post(`/proof-of-delivery/trips/${tripId}/billing-ready`, { comment });
    return response.data?.data || response.data;
  },
};

export const operationalLedgerAPI = {
  async verify() {
    const response = await api.get('/operational-ledger/verify', { skipToast: true });
    return response.data?.data || response.data;
  },
};

export const dataFintechAPI = {
  async getScore() {
    const response = await api.get('/ecarbu-score', { skipToast: true });
    return response.data?.data || response.data;
  },
  async recomputeScore() {
    const response = await api.post('/ecarbu-score/recompute');
    return response.data?.data || response.data;
  },
  async downloadBankAuditReport() {
    const response = await api.get('/bank-audit-report.pdf', {
      responseType: 'blob',
      skipToast: true,
    });
    return response.data;
  },
};

export const arbitrationAPI = {
  async create(tripId) {
    const response = await api.post(`/arbitration-certificates/${tripId}`);
    return response.data?.data || response.data;
  },
  async get(tripId) {
    const response = await api.get(`/arbitration-certificates/${tripId}`, { skipToast: true });
    return response.data?.data || response.data;
  },
  getPdfUrl(tripId) {
    return `${API_BASE}/arbitration-certificates/${tripId}.pdf`;
  },
};

export const clientPortalAPI = {
  async getMissions() {
    const response = await api.get('/client-portal/missions', { skipToast: true });
    return response.data?.data || response.data;
  },
  async getDocuments() {
    const response = await api.get('/client-portal/documents', { skipToast: true });
    return response.data?.data || response.data;
  },
  // Téléchargement avec le jeton du client: l'adresse brute `/documents/files/…`
  // est refusée à ce rôle, seule cette route vérifie la pièce contre son accès.
  async downloadDocument(documentId) {
    return api.get(`/client-portal/documents/${documentId}/download`, {
      responseType: 'blob',
      skipToast: true,
    });
  },
};

// Maintenance du parc : relevés compteur, plans préventifs, ordres de
// travail, pannes, échéances documentaires et immobilisations.
// Every maintenance endpoint answers {success, data}; older endpoints in this
// codebase answer the bare payload, so unwrap defensively like controlFuelAPI.
const unwrap = (response) => response.data?.data ?? response.data;

export const maintenanceAPI = {
  async getOverview() {
    return unwrap(await api.get('/maintenance/overview'));
  },

  // Counters
  async getReadings(params = {}) {
    return unwrap(await api.get('/maintenance/readings', { params }));
  },
  async getLatestReadings() {
    return unwrap(await api.get('/maintenance/readings/latest'));
  },
  async createReading(data) {
    return unwrap(await api.post('/maintenance/readings', data));
  },

  // Preventive plans
  async getPlans(params = {}) {
    return unwrap(await api.get('/maintenance/plans', { params }));
  },
  async createPlan(data) {
    return unwrap(await api.post('/maintenance/plans', data));
  },
  async updatePlan(id, data) {
    return unwrap(await api.patch(`/maintenance/plans/${id}`, data));
  },

  // Work orders
  async getWorkOrders(params = {}) {
    return unwrap(await api.get('/maintenance/work-orders', { params }));
  },
  async createWorkOrder(data) {
    return unwrap(await api.post('/maintenance/work-orders', data));
  },
  async updateWorkOrder(id, data) {
    return unwrap(await api.patch(`/maintenance/work-orders/${id}`, data));
  },
  async closeWorkOrder(id, data = {}) {
    return unwrap(await api.post(`/maintenance/work-orders/${id}/close`, data));
  },
  async getParts(workOrderId) {
    return unwrap(await api.get(`/maintenance/work-orders/${workOrderId}/parts`));
  },
  async addPart(workOrderId, data) {
    return unwrap(await api.post(`/maintenance/work-orders/${workOrderId}/parts`, data));
  },

  // Breakdowns
  async getBreakdowns(params = {}) {
    return unwrap(await api.get('/maintenance/breakdowns', { params }));
  },
  async reportBreakdown(data) {
    return unwrap(await api.post('/maintenance/breakdowns', data));
  },
  async updateBreakdown(id, data) {
    return unwrap(await api.patch(`/maintenance/breakdowns/${id}`, data));
  },

  // Document deadlines
  async getVehicleDocuments(params = {}) {
    return unwrap(await api.get('/maintenance/documents', { params }));
  },
  async createVehicleDocument(data) {
    return unwrap(await api.post('/maintenance/documents', data));
  },
  async updateVehicleDocument(id, data) {
    return unwrap(await api.patch(`/maintenance/documents/${id}`, data));
  },

  // Immobilisation
  async getImmobilizations(params = {}) {
    return unwrap(await api.get('/maintenance/immobilizations', { params }));
  },
  async immobilize(data) {
    return unwrap(await api.post('/maintenance/immobilizations', data));
  },
  async releaseImmobilization(id, note) {
    return unwrap(await api.post(`/maintenance/immobilizations/${id}/release`, { note }));
  },
  async enforceDocumentDeadlines() {
    return unwrap(await api.post('/maintenance/enforce-documents'));
  },

  // ------------------------------------------------------------------ //
  // Pagination serveur, indicateurs et points d'entrée restés orphelins //
  // ------------------------------------------------------------------ //

  /**
   * Les listes maintenance sont paginées côté serveur, et `unwrap` ne rend
   * que `data`: le bloc `pagination` — le seul moyen de savoir qu'il reste
   * des lignes derrière les 50 premières — était jeté en route, si bien que
   * l'écran affichait une page partielle comme si c'était le tout. Ces
   * variantes rendent la page et son compte ensemble.
   */
  async getReadingsPage(params = {}) {
    return unwrapMaintenancePage(await api.get('/maintenance/readings', { params }));
  },
  async getWorkOrdersPage(params = {}) {
    return unwrapMaintenancePage(await api.get('/maintenance/work-orders', { params }));
  },
  async getBreakdownsPage(params = {}) {
    return unwrapMaintenancePage(await api.get('/maintenance/breakdowns', { params }));
  },
  async getVehicleDocumentsPage(params = {}) {
    return unwrapMaintenancePage(await api.get('/maintenance/documents', { params }));
  },
  async getImmobilizationsPage(params = {}) {
    return unwrapMaintenancePage(await api.get('/maintenance/immobilizations', { params }));
  },

  /**
   * Indicateurs de parc. Calculés serveur sur l'ensemble des lignes: un taux
   * d'immobilisation déduit d'une page de 50 est faux, et il l'est en
   * silence.
   */
  async getKpis(params = {}) {
    return unwrap(await api.get('/maintenance/kpis', { params }));
  },

  /**
   * Ouvre tout de suite les ordres préventifs échus, sans attendre le passage
   * nocturne du planificateur.
   */
  async generateDueWorkOrders() {
    return unwrap(await api.post('/maintenance/generate-due-work-orders'));
  },
};

/**
 * Extrait `{ items, pagination }` d'une réponse paginée maintenance.
 *
 * Le total est renvoyé parce que sans lui une page pleine et une dernière
 * page sont indiscernables — c'est exactement l'ambiguïté qui faisait passer
 * 50 lignes pour la totalité du parc.
 */
const unwrapMaintenancePage = (response) => {
  const body = response?.data ?? {};
  const items = Array.isArray(body.data) ? body.data : Array.isArray(body) ? body : [];
  const pagination = body.pagination ?? null;
  return {
    items,
    page: pagination?.page ?? 1,
    pageSize: pagination?.page_size ?? items.length,
    total: pagination?.total ?? items.length,
    pages: pagination?.pages ?? 1,
  };
};

/**
 * Terrain — the read side of what the driver app records on the road.
 *
 * The aggregation (temps d'attente médian par poste, taux de conformité des
 * départs) is done server-side: the browser must not download a border
 * history to compute a median it will show as one number.
 */
export const fieldOpsAPI = {
  async getOverview(params = {}) {
    return unwrap(await api.get('/driver/field-ops/overview', { params }));
  },
  async getBorderEvents(params = {}) {
    return unwrap(await api.get('/driver/field-ops/border-events', { params }));
  },
  async getIncidents(params = {}) {
    return unwrap(await api.get('/driver/field-ops/incidents', { params }));
  },
  async getDepartureChecklists(params = {}) {
    return unwrap(await api.get('/driver/field-ops/departure-checklists', { params }));
  },
  async getGaugeReadings(params = {}) {
    return unwrap(await api.get('/driver/field-ops/gauge-readings', { params }));
  },
};

export const controlFuelAPI = {
  async getSites() {
    const response = await api.get('/control-fuel/sites');
    return response.data?.data || response.data;
  },
  async createSite(data) {
    const response = await api.post('/control-fuel/sites', data);
    return response.data?.data || response.data;
  },
  async getAssets() {
    const response = await api.get('/control-fuel/assets');
    return response.data?.data || response.data;
  },
  async createAsset(data) {
    const response = await api.post('/control-fuel/assets', data);
    return response.data?.data || response.data;
  },
  async getRequests() {
    const response = await api.get('/control-fuel/fuel-requests');
    return response.data?.data || response.data;
  },
  async createRequest(data) {
    const response = await api.post('/control-fuel/fuel-requests', data);
    return response.data?.data || response.data;
  },
  async approveRequest(id) {
    const response = await api.patch(`/control-fuel/fuel-requests/${id}/approve`);
    return response.data?.data || response.data;
  },
  async getIssues() {
    const response = await api.get('/control-fuel/fuel-issues');
    return response.data?.data || response.data;
  },
  async createIssue(data) {
    const response = await api.post('/control-fuel/fuel-issues', data);
    return response.data?.data || response.data;
  },
  async getQuotas() {
    const response = await api.get('/control-fuel/fuel-quotas');
    return response.data?.data || response.data;
  },
  async createQuota(data) {
    const response = await api.post('/control-fuel/fuel-quotas', data);
    return response.data?.data || response.data;
  },
  async getSummary() {
    const response = await api.get('/control-fuel/consumption-summary');
    return response.data?.data || response.data;
  },
  async downloadReportPdf() {
    return await api.get('/control-fuel/report.pdf', { responseType: 'blob' });
  },
};

// Trucks API
export const trucksAPI = {
  async getAll(params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get('/trucks', { params });
        await trucksDB.bulkUpsert(response.data);
        return response.data;
      } catch (error) {
        shouldFallbackOffline(error, 'trucks');
      }
    }
    return await trucksDB.getAll();
  },

  async getById(id) {
    if (canCallApi()) {
      try {
        const response = await api.get(`/trucks/${id}`);
        await trucksDB.upsert(response.data);
        return response.data;
      } catch (error) {
        console.warn('Falling back to local data');
      }
    }
    return await trucksDB.getById(id);
  },

  /**
   * Fiche camion: identité, statut affiché, immobilisation, papiers, alertes,
   * trajet en cours et compteurs. Lecture seule et uniquement en ligne — rien
   * de monétaire dedans, et le cache local n'a pas ces agrégats.
   */
  async getProfile(id) {
    // La page affiche elle-même l'échec (hors ligne): pas de toast global.
    const response = await api.get(`/trucks/${id}/profile`, { skipToast: true });
    return response.data?.data || response.data;
  },

  async create(data) {
    const truckId = crypto.randomUUID();
    const now = new Date().toISOString();

    const matriculeNormalized = String(data.matricule || '')
      .trim()
      .toUpperCase();

    // Optional: create/reuse driver by phone and link it to the truck
    let defaultDriverId = data.default_driver_id || null;
    const driverPhone = (data.driver_phone || '').trim();
    const driverFullName = (data.driver_full_name || '').trim();
    let shouldQueueDriverCreate = false;

    if (driverPhone) {
      const existingDriver = await driversDB.getByPhone(driverPhone);
      if (existingDriver?.id) {
        defaultDriverId = existingDriver.id;
      } else {
        const driverId = crypto.randomUUID();
        defaultDriverId = driverId;
        const driver = {
          id: driverId,
          full_name: driverFullName || 'Chauffeur',
          phone: driverPhone,
          active: true,
          created_at: now,
          updated_at: now,
        };
        await driversDB.upsert(driver);
        shouldQueueDriverCreate = true;
      }
    }

    const truck = {
      id: truckId,
      ...data,
      default_driver_id: defaultDriverId,
      matricule: matriculeNormalized,
      created_at: now,
      updated_at: now,
    };

    await trucksDB.upsert(truck);

    const payload = {
      ...data,
      id: truckId,
      default_driver_id: defaultDriverId,
      matricule: matriculeNormalized,
      driver_full_name: driverFullName || null,
      driver_phone: driverPhone || null,
    };

    if (canCallApi()) {
      try {
        const response = await api.post('/trucks', payload);
        const serverTruck = response.data;
        // The backend may auto-join an existing truck (different ID).
        // Drop the local optimistic placeholder if so.
        if (serverTruck?.id && serverTruck.id !== truckId) {
          try {
            await trucksDB.deleteById(truckId);
          } catch (_) {
            /* noop */
          }
        }
        await trucksDB.upsert(serverTruck);
        return serverTruck;
      } catch (error) {
        if (error?.response) throw error;
        if (shouldQueueDriverCreate) {
          const driver = await driversDB.getById(defaultDriverId);
          if (driver) await syncQueue.add('driver', 'create', driver);
        }
        await syncQueue.add('truck', 'create', truck);
        return truck;
      }
    }

    if (shouldQueueDriverCreate) {
      const driver = await driversDB.getById(defaultDriverId);
      if (driver) await syncQueue.add('driver', 'create', driver);
    }
    await syncQueue.add('truck', 'create', truck);
    return truck;
  },

  
  async addExpense(id, expenseData) {
    if (canCallApi()) {
      const response = await api.post(`/trips/${id}/expenses`, expenseData);
      await tripsDB.upsert(response.data);
      return response.data;
    }
    // We could handle this offline, but for simplicity of the ledger we just force online for now or do a soft update
    throw new Error('You must be online to add itemized expenses.');
  },

  async deleteExpense(id, expenseId) {
    if (canCallApi()) {
      const response = await api.delete(`/trips/${id}/expenses/${expenseId}`);
      await tripsDB.upsert(response.data);
      return response.data;
    }
    throw new Error('You must be online to delete itemized expenses.');
  },

  async update(id, data) {
    const now = new Date().toISOString();
    const updateData = { ...data, updated_at: now };

    if (canCallApi()) {
      try {
        const response = await api.patch(`/trucks/${id}`, data);
        await trucksDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        const existing = await trucksDB.getById(id);
        const updated = { ...existing, ...updateData };
        await trucksDB.upsert(updated);
        // On ne met en file que ce qui a changé: l'enregistrement local est
        // l'aplati de la fiche (org_overrides, statuts calculés...) et le
        // rejouer écraserait des champs que personne n'a touchés.
        await syncQueue.add('truck', 'update', { id, ...updateData });
        return updated;
      }
    } else {
      const existing = await trucksDB.getById(id);
      const updated = { ...existing, ...updateData };
      await trucksDB.upsert(updated);
      await syncQueue.add('truck', 'update', { id, ...updateData });
      return updated;
    }
  },

  async delete(id) {
    const existing = await trucksDB.getById(id);
    if (existing) {
      const trips = await tripsDB.getByTruckId(id);
      await Promise.all(trips.map(trip => 
        trip?.id ? tripUpdatesDB.deleteByTripId(trip.id) : Promise.resolve()
      ));
      await tripsDB.deleteByTruckId(id);
      await trucksDB.deleteById(id);
    }

    if (canCallApi()) {
      try {
        const response = await api.delete(`/trucks/${id}`);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        await syncQueue.add('truck', 'delete', { id });
        return { id, deleted_offline: true };
      }
    }

    await syncQueue.add('truck', 'delete', { id });
    return { id, deleted_offline: true };
  },

  async search(query) {
    if (query.length < 2) return [];

    if (canCallApi()) {
      try {
        const response = await api.get('/search/trucks', {
          params: { q: query },
        });
        return response.data;
      } catch (error) {
        console.warn('Falling back to local search');
      }
    }
    return await trucksDB.search(query);
  },

  async getByMatricule(matricule) {
    if (canCallApi()) {
      try {
        const response = await api.get(
          `/search/truck/${encodeURIComponent(matricule)}`,
        );
        return response.data;
      } catch (error) {
        console.warn('Falling back to local data');
      }
    }
    const truck = await trucksDB.getByMatricule(matricule);
    if (!truck) return null;

    const trips = await tripsDB.getByTruckId(truck.id);
    const activeTrip = trips.find(
      (t) => !['COMPLETED', 'CANCELLED'].includes(t.status),
    );

    return {
      truck,
      current_trip: activeTrip || null,
      trip_history: trips.slice(0, 10),
    };
  },

  async getAllPaged(params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get('/trucks', {
          params: { ...params, include_total: true },
        });
        await trucksDB.bulkUpsert(response.data);
        const propres = Number.parseInt(response?.headers?.['x-count-propres'], 10);
        const loues = Number.parseInt(response?.headers?.['x-count-loues'], 10);
        // L'état du parc entier (filtres compris), pas celui de la page reçue.
        const enTrajet = Number.parseInt(response?.headers?.['x-count-en-trajet'], 10);
        const disponibles = Number.parseInt(response?.headers?.['x-count-disponibles'], 10);
        const immobilises = Number.parseInt(response?.headers?.['x-count-immobilises'], 10);
        return {
          items: response.data,
          total: getTotalFromHeaders(response),
          counts:
            Number.isNaN(propres) || Number.isNaN(loues) ? null : { propres, loues },
          statuts:
            Number.isNaN(enTrajet) || Number.isNaN(disponibles) || Number.isNaN(immobilises)
              ? null
              : { enTrajet, disponibles, immobilises },
        };
      } catch (error) {
        shouldFallbackOffline(error, 'trucks');
      }
    }
    return { items: await trucksDB.getAll(), total: null };
  },
};

// Companies API (Sociétés)
export const companiesAPI = {
  async getAll(params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get('/companies', { params });
        await companiesDB.bulkUpsert(response.data);
        return response.data;
      } catch (error) {
        shouldFallbackOffline(error, 'companies');
      }
    }
    return await companiesDB.getAll();
  },

  async getById(id) {
    if (canCallApi()) {
      try {
        const response = await api.get(`/companies/${id}`);
        await companiesDB.upsert(response.data);
        return response.data;
      } catch (error) {
        console.warn('Falling back to local data');
      }
    }
    return await companiesDB.getById(id);
  },

  async create(data) {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const company = {
      id,
      ...data,
      name: String(data.name || '').trim(),
      created_at: now,
      updated_at: now,
    };

    await companiesDB.upsert(company);

    const payload = { ...data, id };

    if (canCallApi()) {
      try {
        const response = await api.post('/companies', payload);
        await companiesDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        await syncQueue.add('company', 'create', company);
        return company;
      }
    }

    await syncQueue.add('company', 'create', company);
    return company;
  },

  
  async addExpense(id, expenseData) {
    if (canCallApi()) {
      const response = await api.post(`/trips/${id}/expenses`, expenseData);
      await tripsDB.upsert(response.data);
      return response.data;
    }
    // We could handle this offline, but for simplicity of the ledger we just force online for now or do a soft update
    throw new Error('You must be online to add itemized expenses.');
  },

  async deleteExpense(id, expenseId) {
    if (canCallApi()) {
      const response = await api.delete(`/trips/${id}/expenses/${expenseId}`);
      await tripsDB.upsert(response.data);
      return response.data;
    }
    throw new Error('You must be online to delete itemized expenses.');
  },

  async update(id, data) {
    const now = new Date().toISOString();
    const updateData = { ...data, updated_at: now };

    if (canCallApi()) {
      try {
        const response = await api.put(`/companies/${id}`, { ...data, id });
        await companiesDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        const existing = await companiesDB.getById(id);
        const updated = { ...existing, ...updateData };
        await companiesDB.upsert(updated);
        await syncQueue.add('company', 'update', updated);
        return updated;
      }
    }

    const existing = await companiesDB.getById(id);
    const updated = { ...existing, ...updateData };
    await companiesDB.upsert(updated);
    await syncQueue.add('company', 'update', updated);
    return updated;
  },

  async delete(id) {
    const existing = await companiesDB.getById(id);
    if (existing) {
      await companiesDB.deleteById(id);

      // Unlink trucks locally too
      const allTrucks = await trucksDB.getAll();
      const now = new Date().toISOString();
      const linked = allTrucks.filter((t) => t?.company_id === id);
      await Promise.all(linked.map(truck =>
        trucksDB.upsert({
          ...truck,
          company_id: null,
          company_name: null,
          updated_at: now,
        })
      ));
    }

    if (canCallApi()) {
      try {
        const response = await api.delete(`/companies/${id}`);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        await syncQueue.add('company', 'delete', { id });
        return { id, deleted_offline: true };
      }
    }

    await syncQueue.add('company', 'delete', { id });
    return { id, deleted_offline: true };
  },

  async getAllPaged(params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get('/companies', {
          params: { ...params, include_total: true },
        });
        await companiesDB.bulkUpsert(response.data);
        // Les chiffres de l'en-tête de l'écran portent sur toute la recherche,
        // pas sur la page reçue.
        const compte = (nom) => {
          const n = Number.parseInt(response?.headers?.[nom], 10);
          return Number.isNaN(n) ? null : n;
        };
        const avecCamions = compte('x-count-avec-camions');
        const trajetsActifs = compte('x-count-trajets-actifs');
        const aQualifier = compte('x-count-a-qualifier');
        return {
          items: response.data,
          total: getTotalFromHeaders(response),
          counts:
            avecCamions === null || trajetsActifs === null || aQualifier === null
              ? null
              : { avecCamions, trajetsActifs, aQualifier },
        };
      } catch (error) {
        shouldFallbackOffline(error, 'companies');
      }
    }
    return { items: await companiesDB.getAll(), total: null };
  },
};

// Drivers API
export const driversAPI = {
  async getAll(params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get('/drivers', { params });
        await driversDB.bulkUpsert(response.data);
        return response.data;
      } catch (error) {
        shouldFallbackOffline(error, 'drivers');
      }
    }
    return await driversDB.getAll();
  },

  async getById(id) {
    if (canCallApi()) {
      try {
        const response = await api.get(`/drivers/${id}`);
        await driversDB.upsert(response.data);
        return response.data;
      } catch (error) {
        console.warn('Falling back to local data');
      }
    }
    return await driversDB.getById(id);
  },

  async create(data) {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const driver = { id, ...data, created_at: now, updated_at: now };

    const payload = { ...data, id };

    await driversDB.upsert(driver);

    if (canCallApi()) {
      try {
        const response = await api.post('/drivers', payload);
        await driversDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        await syncQueue.add('driver', 'create', driver);
        return driver;
      }
    }

    await syncQueue.add('driver', 'create', driver);
    return driver;
  },

  
  async addExpense(id, expenseData) {
    if (canCallApi()) {
      const response = await api.post(`/trips/${id}/expenses`, expenseData);
      await tripsDB.upsert(response.data);
      return response.data;
    }
    // We could handle this offline, but for simplicity of the ledger we just force online for now or do a soft update
    throw new Error('You must be online to add itemized expenses.');
  },

  async deleteExpense(id, expenseId) {
    if (canCallApi()) {
      const response = await api.delete(`/trips/${id}/expenses/${expenseId}`);
      await tripsDB.upsert(response.data);
      return response.data;
    }
    throw new Error('You must be online to delete itemized expenses.');
  },

  async update(id, data) {
    const now = new Date().toISOString();

    if (canCallApi()) {
      try {
        const response = await api.patch(`/drivers/${id}`, data);
        await driversDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        const existing = await driversDB.getById(id);
        const updated = { ...existing, ...data, updated_at: now };
        await driversDB.upsert(updated);
        await syncQueue.add('driver', 'update', updated);
        return updated;
      }
    }

    const existing = await driversDB.getById(id);
    const updated = { ...existing, ...data, updated_at: now };
    await driversDB.upsert(updated);
    await syncQueue.add('driver', 'update', updated);
    return updated;
  },

  async delete(id) {
    const existing = await driversDB.getById(id);
    if (existing) {
      const allTrips = await tripsDB.getAll();
      const trips = allTrips.filter((t) => t?.driver_id === id);
      const completedStatuses = new Set(['COMPLETED', 'CANCELLED']);
      await Promise.all(trips.map(async (trip) => {
        const wasActive =
          trip?.status && !completedStatuses.has(trip.status);

        const operations = [];
        if (trip?.id) {
          operations.push(tripUpdatesDB.deleteByTripId(trip.id));
          operations.push(tripsDB.deleteById(trip.id));
        }

        if (wasActive && trip?.truck_id) {
          const truck = await trucksDB.getById(trip.truck_id);
          if (truck) {
            truck.current_status = 'FREE';
            truck.updated_at = new Date().toISOString();
            operations.push(trucksDB.upsert(truck));
          }
        }
      }));
      await driversDB.deleteById(id);
    }

    if (canCallApi()) {
      try {
        const response = await api.delete(`/drivers/${id}`);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        await syncQueue.add('driver', 'delete', { id });
        return { id, deleted_offline: true };
      }
    }

    await syncQueue.add('driver', 'delete', { id });
    return { id, deleted_offline: true };
  },

  async getAllPaged(params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get('/drivers', {
          params: { ...params, include_total: true },
        });
        await driversDB.bulkUpsert(response.data);
        return { items: response.data, total: getTotalFromHeaders(response) };
      } catch (error) {
        shouldFallbackOffline(error, 'drivers');
      }
    }
    return { items: await driversDB.getAll(), total: null };
  },

  async regenerateCode(id) {
    const response = await api.post(`/drivers/${id}/regenerate-code`);
    // Backend wraps in SuccessResponse: { data: { driver_id, user_id, access_code }, ... }
    return response.data?.data || response.data;
  },
};

// Operations API (replaces Partners)
export const operationsAPI = {
  async getAll(params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get('/operations', { params });
        await operationsDB.bulkUpsert(response.data);
        return response.data;
      } catch (error) {
        shouldFallbackOffline(error, 'operations');
      }
    }
    return await operationsDB.getAll();
  },

  async getById(id) {
    if (canCallApi()) {
      try {
        const response = await api.get(`/operations/${id}`);
        await operationsDB.upsert(response.data);
        return response.data;
      } catch (error) {
        console.warn('Falling back to local data');
      }
    }
    return await operationsDB.getById(id);
  },

  async create(data) {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const codeNormalized = String(data.code || '')
      .trim()
      .toUpperCase();
    const operation = {
      id,
      ...data,
      code: codeNormalized,
      created_at: now,
      updated_at: now,
    };

    await operationsDB.upsert(operation);

    const payload = { ...data, id, code: codeNormalized };

    if (canCallApi()) {
      try {
        const response = await api.post('/operations', payload);
        await operationsDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        await syncQueue.add('operation', 'create', operation);
        return operation;
      }
    }

    await syncQueue.add('operation', 'create', operation);
    return operation;
  },

  
  async addExpense(id, expenseData) {
    if (canCallApi()) {
      const response = await api.post(`/trips/${id}/expenses`, expenseData);
      await tripsDB.upsert(response.data);
      return response.data;
    }
    // We could handle this offline, but for simplicity of the ledger we just force online for now or do a soft update
    throw new Error('You must be online to add itemized expenses.');
  },

  async deleteExpense(id, expenseId) {
    if (canCallApi()) {
      const response = await api.delete(`/trips/${id}/expenses/${expenseId}`);
      await tripsDB.upsert(response.data);
      return response.data;
    }
    throw new Error('You must be online to delete itemized expenses.');
  },

  async update(id, data) {
    const now = new Date().toISOString();

    if (canCallApi()) {
      try {
        const response = await api.put(`/operations/${id}`, data);
        await operationsDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        const existing = await operationsDB.getById(id);
        const updated = { ...existing, ...data, updated_at: now };
        await operationsDB.upsert(updated);
        // Seulement les champs modifiés, pas l'enregistrement local complet.
        await syncQueue.add('operation', 'update', { id, ...data });
        return updated;
      }
    } else {
      const existing = await operationsDB.getById(id);
      const updated = { ...existing, ...data, updated_at: now };
      await operationsDB.upsert(updated);
      await syncQueue.add('operation', 'update', { id, ...data });
      return updated;
    }
  },

  async delete(id) {
    const existing = await operationsDB.getById(id);
    if (existing) {
      const allTrips = await tripsDB.getAll();
      const trips = allTrips.filter((t) => t?.operation_id === id);
      const completedStatuses = new Set(['COMPLETED', 'CANCELLED']);
      await Promise.all(trips.map(async (trip) => {
        const wasActive =
          trip?.status && !completedStatuses.has(trip.status);

        const promises = [];
        if (trip?.id) {
          promises.push(tripUpdatesDB.deleteByTripId(trip.id));
          promises.push(tripsDB.deleteById(trip.id));
        }

        if (wasActive && trip?.truck_id) {
          const truck = await trucksDB.getById(trip.truck_id);
          if (truck) {
            truck.current_status = 'FREE';
            truck.updated_at = new Date().toISOString();
            promises.push(trucksDB.upsert(truck));
          }
        }
      }));
      await operationsDB.deleteById(id);
    }

    if (canCallApi()) {
      try {
        const response = await api.delete(`/operations/${id}`);
        return response.data;
      } catch (error) {
        await syncQueue.add('operation', 'delete', { id });
        throw error;
      }
    }

    await syncQueue.add('operation', 'delete', { id });
    return { id, deleted_offline: true };
  },

  async search(query) {
    if (query.length < 2) return [];

    if (canCallApi()) {
      try {
        const response = await api.get('/search/operations', {
          params: { q: query },
        });
        return response.data;
      } catch (error) {
        console.warn('Falling back to local search');
      }
    }
    return await operationsDB.search(query);
  },

  async getAllPaged(params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get('/operations', {
          params: { ...params, include_total: true },
        });
        await operationsDB.bulkUpsert(response.data);
        // Les chiffres de l'en-tête de l'écran portent sur toute la recherche,
        // pas sur la page reçue.
        const compte = (nom) => {
          const n = Number.parseInt(response?.headers?.[nom], 10);
          return Number.isNaN(n) ? null : n;
        };
        const enCours = compte('x-count-en-cours');
        const termines = compte('x-count-termines');
        const sansCamion = compte('x-count-sans-camion');
        return {
          items: response.data,
          total: getTotalFromHeaders(response),
          counts:
            enCours === null || termines === null || sansCamion === null
              ? null
              : { enCours, termines, sansCamion },
        };
      } catch (error) {
        shouldFallbackOffline(error, 'operations');
      }
    }
    return { items: await operationsDB.getAll(), total: null };
  },

  async getNextCode() {
    if (canCallApi()) {
      try {
        const response = await api.get('/operations/next-code');
        return response.data.code;
      } catch (error) {
        console.error('Failed to fetch next code', error);
        throw error;
      }
    }
    throw new Error('Next code generation requires online connection');
  },
};

// Clients API
export const clientsAPI = {
  async getAllPaged(params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get('/clients/', { params });
        const items = response.data?.data || response.data || [];
        await clientsDB.bulkUpsert(items);
        return {
          items,
          total:
            response.data?.meta?.total_items ?? getTotalFromHeaders(response),
        };
      } catch (error) {
        shouldFallbackOffline(error, 'clients');
      }
    }

    return { items: await clientsDB.getAll(), total: null };
  },

  async getById(id) {
    if (canCallApi()) {
      try {
        const response = await api.get(`/clients/${id}`);
        await clientsDB.upsert(response.data);
        return response.data;
      } catch (error) {
        console.warn('Falling back to local data for client');
      }
    }
    return await clientsDB.getById(id);
  },

  async create(data) {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();

    if (canCallApi()) {
      try {
        // The backend write route is declared with a trailing slash. Calling
        // it explicitly avoids a 307 redirect that some production proxies
        // do not replay for POST requests.
        const response = await api.post('/clients/', {
          status: 'ACTIVE',
          payment_terms: 'CASH',
          ...data,
          id,
        });
        await clientsDB.upsert(response.data);
        return response.data;
      } catch (error) {
        // A server response means the write was refused (validation, duplicate,
        // permissions, etc.), not that we are offline. Queueing it locally in
        // that case made the UI show a phantom client which disappeared after
        // refresh because it never existed on the server.
        if (error?.response) throw error;
        // Only transport failures may fall through to the offline queue.
      }
    }

    const client = {
      id,
      ...data,
      created_at: now,
      updated_at: now,
    };
    await clientsDB.upsert(client);
    await syncQueue.add('client', 'create', client);
    return client;
  },

  
  async addExpense(id, expenseData) {
    if (canCallApi()) {
      const response = await api.post(`/trips/${id}/expenses`, expenseData);
      await tripsDB.upsert(response.data);
      return response.data;
    }
    // We could handle this offline, but for simplicity of the ledger we just force online for now or do a soft update
    throw new Error('You must be online to add itemized expenses.');
  },

  async deleteExpense(id, expenseId) {
    if (canCallApi()) {
      const response = await api.delete(`/trips/${id}/expenses/${expenseId}`);
      await tripsDB.upsert(response.data);
      return response.data;
    }
    throw new Error('You must be online to delete itemized expenses.');
  },

  async update(id, data) {
    const now = new Date().toISOString();

    if (canCallApi()) {
      try {
        const response = await api.put(`/clients/${id}`, data);
        await clientsDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }

    const existing = await clientsDB.getById(id);
    const updated = { ...existing, ...data, updated_at: now };
    await clientsDB.upsert(updated);
    await syncQueue.add('client', 'update', updated);
    return updated;
  },

  // Messages WhatsApp au client final aux étapes de ses voyages (lot 053):
  // réservé à l'administrateur, jamais mis en file hors ligne.
  async setTripNotifications(id, enabled) {
    const response = await api.put(`/clients/${id}/trip-notifications`, { enabled });
    await clientsDB.upsert(response.data);
    return response.data;
  },

  async delete(id) {
    if (canCallApi()) {
      try {
        const response = await api.delete(`/clients/${id}`);
        await clientsDB.deleteById(id);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }

    await clientsDB.deleteById(id);
    await syncQueue.add('client', 'delete', { id });
    return { success: true, queued: true };
  },
};

// Orders API
export const ordersAPI = {
  async getAllPaged(params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get('/orders', { params });
        const items = response.data?.data || response.data || [];
        await ordersDB.bulkUpsert(items);
        return {
          items,
          total:
            response.data?.meta?.total_items ?? getTotalFromHeaders(response),
        };
      } catch (error) {
        shouldFallbackOffline(error, 'orders');
      }
    }
    return { items: await ordersDB.getAll(), total: null };
  },

  async getById(id) {
    if (canCallApi()) {
      try {
        const response = await api.get(`/orders/${id}`);
        await ordersDB.upsert(response.data);
        return response.data;
      } catch (error) {
        console.warn('Falling back to local data for order');
      }
    }
    return await ordersDB.getById(id);
  },

  async create(data) {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();

    if (canCallApi()) {
      try {
        const response = await api.post('/orders', { ...data, id });
        await ordersDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }

    const order = {
      id,
      ...data,
      status: data.status || 'DRAFT',
      order_date: data.order_date || now,
      created_at: now,
      updated_at: now,
    };
    await ordersDB.upsert(order);
    await syncQueue.add('order', 'create', order);
    return order;
  },

  
  async addExpense(id, expenseData) {
    if (canCallApi()) {
      const response = await api.post(`/trips/${id}/expenses`, expenseData);
      await tripsDB.upsert(response.data);
      return response.data;
    }
    // We could handle this offline, but for simplicity of the ledger we just force online for now or do a soft update
    throw new Error('You must be online to add itemized expenses.');
  },

  async deleteExpense(id, expenseId) {
    if (canCallApi()) {
      const response = await api.delete(`/trips/${id}/expenses/${expenseId}`);
      await tripsDB.upsert(response.data);
      return response.data;
    }
    throw new Error('You must be online to delete itemized expenses.');
  },

  async update(id, data) {
    const now = new Date().toISOString();

    if (canCallApi()) {
      try {
        const response = await api.patch(`/orders/${id}`, data);
        await ordersDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }

    const existing = await ordersDB.getById(id);
    const updated = { ...existing, ...data, updated_at: now };
    await ordersDB.upsert(updated);
    await syncQueue.add('order', 'update', updated);
    return updated;
  },

  async delete(id) {
    if (canCallApi()) {
      try {
        const response = await api.delete(`/orders/${id}`);
        await ordersDB.deleteById(id);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }

    await ordersDB.deleteById(id);
    await syncQueue.add('order', 'delete', { id });
    return { success: true, queued: true };
  },

  // A server that answered and refused is not an offline situation. Queueing
  // its refusal used to turn "la quantité dépasse le volume restant" into
  // `{success: true, queued: true}` — the dispatcher was told the allocation
  // had gone through, and the same rejected write was replayed by the sync
  // later. Only a transport failure (no response, 5xx, timeout, throttling)
  // may fall through to the queue.
  async allocate(orderId, tripId, quantity) {
    const payload = {
      id: crypto.randomUUID(),
      order_id: orderId,
      trip_id: tripId,
      quantity,
    };

    if (canCallApi()) {
      try {
        const response = await api.post(`/orders/${orderId}/allocate`, null, {
          params: { trip_id: tripId, quantity },
          // The dispatch screen reports allocation outcomes itself, per truck
          // and aggregated: the interceptor's one-toast-per-request would fire
          // N times on a batch.
          skipToast: true,
        });
        return response.data;
      } catch (error) {
        if (isReproducibleRefusal(error)) throw error;
        // transport failure: fall through to offline queue
      }
    }

    await syncQueue.add('order', 'allocate', payload);
    return { success: true, queued: true };
  },

  async autoAllocate(orderId) {
    if (canCallApi()) {
      try {
        const response = await api.post(`/orders/${orderId}/auto-allocate`);
        return response.data;
      } catch (error) {
        if (isReproducibleRefusal(error)) throw error;
        // transport failure: fall through to offline queue
      }
    }

    await syncQueue.add('order', 'auto_allocate', { id: orderId });
    return { success: true, queued: true };
  },

  async getAvailableTrucks(orderId) {
    if (canCallApi()) {
      try {
        const response = await api.get(`/orders/${orderId}/available-trucks`);
        return response.data?.data || [];
      } catch (error) {
        console.warn('Failed to load available trucks');
      }
    }
    return [];
  },

  /**
   * Les camions disponibles **avant** que la commande n'existe.
   *
   * L'écran propose les camions pendant la saisie: créer la commande, la
   * retrouver dans la liste, puis l'ouvrir pour lui poser des camions fait
   * trois gestes là où l'exploitant en pense un seul.
   */
  async getAvailableTrucksForDraft({ quantityLiters = 0, destination } = {}) {
    if (!canCallApi()) return [];
    try {
      const response = await api.get('/orders/available-trucks', {
        params: {
          quantity_liters: quantityLiters,
          ...(destination ? { destination } : {}),
        },
      });
      return response.data?.data || [];
    } catch (error) {
      console.warn('Failed to load available trucks for draft');
      return [];
    }
  },

  /**
   * Confirmer une commande. L'endpoint existait depuis l'origine et n'était
   * appelé par personne: l'écran faisait changer le statut à la main dans une
   * liste déroulante, ce qui contournait la transition et son journal.
   */
  async confirm(orderId) {
    const response = await api.patch(`/orders/${orderId}/confirm`);
    return response.data;
  },

  async cancel(orderId, reason) {
    const response = await api.patch(`/orders/${orderId}/cancel`, null, {
      params: { reason },
    });
    return response.data;
  },

  /**
   * Les camions partis livrer dont la commande n'est pas soldée.
   *
   * Le camion est libéré dès la douane d'entrée franchie: il repart sur une
   * nouvelle importation avant d'avoir remis sa cargaison. Rien ne referme
   * alors le trajet de livraison si personne ne solde la commande.
   */
  async getToSettle() {
    if (!canCallApi()) return { lignes: [], maxDays: 0 };
    try {
      const response = await api.get('/orders/to-settle');
      return {
        lignes: response.data?.data || [],
        maxDays: response.data?.max_days || 0,
      };
    } catch (error) {
      console.warn('Failed to load unsettled deliveries');
      return { lignes: [], maxDays: 0 };
    }
  },

  /** Les camions d'une commande, avec ce qui leur reste à dépoter. */
  async getAllocations(orderId) {
    if (!canCallApi()) return [];
    try {
      const response = await api.get(`/orders/${orderId}/allocations`);
      return response.data?.data || [];
    } catch (error) {
      console.warn('Failed to load order allocations');
      return [];
    }
  },

  async getDispatchReadiness(orderId) {
    if (canCallApi()) {
      const response = await api.get(`/orders/${orderId}/dispatch-readiness`);
      return response.data?.data || response.data;
    }
    return null;
  },

  async recordDelivery(orderId, delivery) {
    const deliveryId = delivery.delivery_id || crypto.randomUUID();
    const body = { ...delivery, delivery_id: deliveryId, delivery_date: delivery.delivery_date || new Date().toISOString() };
    const payload = { ...body, id: deliveryId, order_id: orderId };

    if (canCallApi()) {
      try {
        const response = await api.post(
          `/orders/${orderId}/deliveries`,
          body,
        );
        await ordersDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }

    await syncQueue.add('order', 'delivery', payload);
    const existing = await ordersDB.getById(orderId);
    if (existing) {
      const now = new Date().toISOString();
      const delivered =
        (existing.delivered_quantity || 0) + (delivery.quantity_liters || 0);
      const remaining = Math.max(
        (existing.quantity_liters || 0) - delivered,
        0,
      );
      const updated = {
        ...existing,
        delivered_quantity: delivered,
        remaining_quantity: remaining,
        status: remaining === 0 ? 'DELIVERED' : 'PARTIALLY_DELIVERED',
        updated_at: now,
      };
      await ordersDB.upsert(updated);
      return updated;
    }

    return { success: true, queued: true };
  },
};

// Invoices API
// Les factures (montants, statut, règlements, émission) ne passent jamais par
// l'ancienne file /sync: seuls les brouillons s'écrivent sans réseau, par la
// passerelle locale (lib/offline/writeRoutes.js).
export const invoicesAPI = {
  async getAllPaged(params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get('/invoices', { params });
        const items = response.data?.data || response.data || [];
        await invoicesDB.bulkUpsert(items);
        return {
          items,
          total:
            response.data?.meta?.total_items ?? getTotalFromHeaders(response),
        };
      } catch (error) {
        shouldFallbackOffline(error, 'invoices');
      }
    }
    return { items: await invoicesDB.getAll(), total: null };
  },

  async getById(id) {
    if (canCallApi()) {
      try {
        const response = await api.get(`/invoices/${id}`);
        await invoicesDB.upsert(response.data);
        return response.data;
      } catch (error) {
        console.warn('Falling back to local data for invoice');
      }
    }
    return await invoicesDB.getById(id);
  },

  async create(data) {
    const id = crypto.randomUUID();

    if (canCallApi()) {
      try {
        const response = await api.post('/invoices', { ...data, id });
        await invoicesDB.upsert(response.data);
        return response.data;
      } catch (error) {
        throw error;
      }
    }

    throw networkError();
  },

  
  async addExpense(id, expenseData) {
    if (canCallApi()) {
      const response = await api.post(`/trips/${id}/expenses`, expenseData);
      await tripsDB.upsert(response.data);
      return response.data;
    }
    // We could handle this offline, but for simplicity of the ledger we just force online for now or do a soft update
    throw new Error('You must be online to add itemized expenses.');
  },

  async deleteExpense(id, expenseId) {
    if (canCallApi()) {
      const response = await api.delete(`/trips/${id}/expenses/${expenseId}`);
      await tripsDB.upsert(response.data);
      return response.data;
    }
    throw new Error('You must be online to delete itemized expenses.');
  },

  async update(id, data) {
    if (canCallApi()) {
      try {
        const response = await api.patch(`/invoices/${id}`, data);
        await invoicesDB.upsert(response.data);
        return response.data;
      } catch (error) {
        throw error;
      }
    }

    throw networkError();
  },

  async delete(id) {
    if (canCallApi()) {
      try {
        const response = await api.delete(`/invoices/${id}`);
        await invoicesDB.deleteById(id);
        return response.data;
      } catch (error) {
        throw error;
      }
    }

    throw networkError();
  },

  async recordPayment(id, payment) {
    if (canCallApi()) {
      try {
        const response = await api.post(`/invoices/${id}/payments`, null, {
          params: payment,
        });
        await invoicesDB.upsert(response.data);
        return response.data;
      } catch (error) {
        throw error;
      }
    }

    // Un règlement ne s'enregistre qu'avec le serveur: jamais affiché encaissé
    // avant qu'il l'ait accepté, jamais mis en file.
    throw networkError();
  },

  async downloadPdf(id) {
    return await api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
  },

  async generateForOrder(orderId) {
    const response = await api.post(`/invoices/orders/${orderId}/generate`);
    const invoice = response.data?.data || response.data;
    if (invoice?.id) {
      await invoicesDB.upsert(invoice);
    }
    return invoice;
  },

  async dispatch(id, payload) {
    const response = await api.post(`/invoices/${id}/dispatch`, payload);
    const data = response.data?.data || response.data;
    if (data?.invoice?.id) {
      await invoicesDB.upsert(data.invoice);
    }
    return data;
  },
};

// Stock Movements API
export const stockMovementsAPI = {
  async getAllPaged(params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get('/stock', { params });
        const items = response.data?.data || response.data || [];
        await stockMovementsDB.bulkUpsert(items);
        return {
          items,
          total:
            response.data?.meta?.total_items ?? getTotalFromHeaders(response),
        };
      } catch (error) {
        shouldFallbackOffline(error, 'stock movements');
      }
    }
    return { items: await stockMovementsDB.getAll(), total: null };
  },

  async create(data) {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();

    if (canCallApi()) {
      try {
        const response = await api.post('/stock', { ...data, id });
        await stockMovementsDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }

    const movement = {
      id,
      ...data,
      movement_date: data.movement_date || now,
      created_at: now,
      updated_at: now,
    };
    await stockMovementsDB.upsert(movement);
    await syncQueue.add('stock_movement', 'create', movement);
    return movement;
  },

  
  async addExpense(id, expenseData) {
    if (canCallApi()) {
      const response = await api.post(`/trips/${id}/expenses`, expenseData);
      await tripsDB.upsert(response.data);
      return response.data;
    }
    // We could handle this offline, but for simplicity of the ledger we just force online for now or do a soft update
    throw new Error('You must be online to add itemized expenses.');
  },

  async deleteExpense(id, expenseId) {
    if (canCallApi()) {
      const response = await api.delete(`/trips/${id}/expenses/${expenseId}`);
      await tripsDB.upsert(response.data);
      return response.data;
    }
    throw new Error('You must be online to delete itemized expenses.');
  },

  async update(id, data) {
    const now = new Date().toISOString();

    if (canCallApi()) {
      try {
        const response = await api.patch(`/stock/${id}`, data);
        await stockMovementsDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }

    const existing = await stockMovementsDB.getById(id);
    const updated = { ...existing, ...data, updated_at: now };
    await stockMovementsDB.upsert(updated);
    await syncQueue.add('stock_movement', 'update', updated);
    return updated;
  },

  async delete(id) {
    if (canCallApi()) {
      try {
        const response = await api.delete(`/stock/${id}`);
        await stockMovementsDB.deleteById(id);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }

    await stockMovementsDB.deleteById(id);
    await syncQueue.add('stock_movement', 'delete', { id });
    return { success: true, queued: true };
  },

  async getSummary(params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get('/stock/summary', { params });
        return response.data;
      } catch (error) {
        console.error('Failed to fetch stock summary', error);
        throw error;
      }
    }
    throw new Error('Stock summary requires online connection');
  },

  async getReadiness(params = {}) {
    if (canCallApi()) {
      const response = await api.get('/stock/readiness', { params });
      return response.data?.data || response.data;
    }
    return null;
  },

  async recompute() {
    if (canCallApi()) {
      try {
        const response = await api.post('/stock/recompute');
        // loadMovements is a component-level function and does not exist in
        // the API layer: this threw a ReferenceError *after* the POST had
        // succeeded, so a recompute that worked was reported to the user as a
        // failure. Reloading belongs to whoever called this.
        return response.data;
      } catch (error) {
        console.error('Failed to recompute stock', error);
        throw error;
      }
    }
    throw new Error('Stock recompute requires online connection');
  },
};

// Station Service API
export const stationsAPI = {
  async getAllPaged(params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get('/stations', { params });
        const items = response.data?.data || response.data || [];
        await stationsDB.bulkUpsert(items);
        return {
          items,
          total:
            response.data?.meta?.total_items ?? getTotalFromHeaders(response),
        };
      } catch (error) {
        shouldFallbackOffline(error, 'stations');
      }
    }
    return { items: await stationsDB.getAll(), total: null };
  },

  async getById(id) {
    if (canCallApi()) {
      try {
        const response = await api.get(`/stations/${id}`);
        await stationsDB.upsert(response.data);
        return response.data;
      } catch (error) {
        console.warn('Falling back to local station details');
      }
    }
    return await stationsDB.getById(id);
  },

  async create(data) {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();

    if (canCallApi()) {
      try {
        const response = await api.post('/stations', { ...data, id });
        await stationsDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }

    const station = {
      id,
      ...data,
      created_at: now,
      updated_at: now,
      status: data.status || 'ACTIVE',
    };
    await stationsDB.upsert(station);
    await syncQueue.add('station', 'create', station);
    return station;
  },

  async update(id, data) {
    const now = new Date().toISOString();

    if (canCallApi()) {
      try {
        const response = await api.patch(`/stations/${id}`, data);
        await stationsDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }

    const existing = await stationsDB.getById(id);
    const updated = { ...existing, ...data, updated_at: now };
    await stationsDB.upsert(updated);
    await syncQueue.add('station', 'update', updated);
    return updated;
  },

  async delete(id) {
    if (canCallApi()) {
      try {
        const response = await api.delete(`/stations/${id}`);
        await stationsDB.deleteById(id);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }

    await stationsDB.deleteById(id);
    await syncQueue.add('station', 'delete', { id });
    return { success: true, queued: true };
  },

  async getTanks(stationId, params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get(`/stations/${stationId}/tanks`, {
          params,
        });
        const items = response.data?.data || response.data || [];
        await stationTanksDB.bulkUpsert(items);
        return items;
      } catch (error) {
        console.warn('Falling back to local tanks');
      }
    }
    return await stationTanksDB.getByStationId(stationId);
  },

  async getAllTanks(params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get('/stations/tanks', { params });
        const items = response.data?.data || response.data || [];
        await stationTanksDB.bulkUpsert(items);
        return items;
      } catch (error) {
        console.warn('Falling back to local all tanks');
      }
    }
    return await stationTanksDB.getAll();
  },

  async createTankOptionalStation(data) {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    if (canCallApi()) {
      try {
        const response = await api.post('/stations/tanks', { ...data, id });
        await stationTanksDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }

    const tank = {
      id,
      ...data,
      station_id: data.station_id || null,
      current_volume_liters: Number(data.current_volume_liters || 0),
      created_at: now,
      updated_at: now,
    };
    await stationTanksDB.upsert(tank);
    await syncQueue.add('station_tank', 'create', tank);
    return tank;
  },

  async createTank(stationId, data) {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    if (canCallApi()) {
      try {
        const response = await api.post(`/stations/${stationId}/tanks`, {
          ...data,
          id,
        });
        await stationTanksDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }

    const tank = {
      id,
      station_id: stationId,
      ...data,
      current_volume_liters: Number(data.current_volume_liters || 0),
      created_at: now,
      updated_at: now,
    };
    await stationTanksDB.upsert(tank);
    await syncQueue.add('station_tank', 'create', tank);
    return tank;
  },

  async getPumps(stationId, params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get(`/stations/${stationId}/pumps`, {
          params,
        });
        const items = response.data?.data || response.data || [];
        await stationPumpsDB.bulkUpsert(items);
        return items;
      } catch (error) {
        console.warn('Falling back to local pumps');
      }
    }
    return await stationPumpsDB.getByStationId(stationId);
  },

  async createPump(stationId, data) {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    if (canCallApi()) {
      try {
        const response = await api.post(`/stations/${stationId}/pumps`, {
          ...data,
          id,
        });
        await stationPumpsDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }

    const pump = {
      id,
      station_id: stationId,
      ...data,
      meter_reading_liters: Number(data.meter_reading_liters || 0),
      created_at: now,
      updated_at: now,
    };
    await stationPumpsDB.upsert(pump);
    await syncQueue.add('station_pump', 'create', pump);
    return pump;
  },

  async getAttendants(stationId, params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get(`/stations/${stationId}/attendants`, {
          params,
        });
        const items = response.data?.data || response.data || [];
        await stationAttendantsDB.bulkUpsert(items);
        return items;
      } catch (error) {
        console.warn('Falling back to local attendants');
      }
    }
    return await stationAttendantsDB.getByStationId(stationId);
  },

  async createAttendant(stationId, data) {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    if (canCallApi()) {
      try {
        const response = await api.post(`/stations/${stationId}/attendants`, {
          ...data,
          id,
        });
        await stationAttendantsDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }

    const attendant = {
      id,
      station_id: stationId,
      ...data,
      is_active: data.is_active ?? true,
      created_at: now,
      updated_at: now,
    };
    await stationAttendantsDB.upsert(attendant);
    await syncQueue.add('station_attendant', 'create', attendant);
    return attendant;
  },

  async updateAttendant(stationId, attendantId, data) {
    const now = new Date().toISOString();
    if (canCallApi()) {
      try {
        const response = await api.patch(
          `/stations/${stationId}/attendants/${attendantId}`,
          data,
        );
        await stationAttendantsDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }

    const attendants = await stationAttendantsDB.getByStationId(stationId);
    const existing = attendants.find((item) => item.id === attendantId) || {
      id: attendantId,
      station_id: stationId,
    };
    const updated = { ...existing, ...data, updated_at: now };
    await stationAttendantsDB.upsert(updated);
    await syncQueue.add('station_attendant', 'update', updated);
    return updated;
  },

  async getPrices(stationId, params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get(`/stations/${stationId}/prices`, {
          params,
        });
        const items = response.data?.data || response.data || [];
        await stationPricesDB.bulkUpsert(items);
        return items;
      } catch (error) {
        console.warn('Falling back to local prices');
      }
    }
    return await stationPricesDB.getByStationId(stationId);
  },

  async createPrice(stationId, data) {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    if (canCallApi()) {
      try {
        const response = await api.post(`/stations/${stationId}/prices`, {
          ...data,
          id,
        });
        await stationPricesDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }
    const price = {
      id,
      station_id: stationId,
      ...data,
      effective_from: data.effective_from || now,
      created_at: now,
    };
    await stationPricesDB.upsert(price);
    await syncQueue.add('station_price', 'create', price);
    return price;
  },

  async getPurchases(stationId, params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get(`/stations/${stationId}/purchases`, {
          params,
        });
        const items = response.data?.data || response.data || [];
        await stationPurchasesDB.bulkUpsert(items);
        return items;
      } catch (error) {
        console.warn('Falling back to local purchases');
      }
    }
    return await stationPurchasesDB.getByStationId(stationId);
  },

  async createPurchase(stationId, data) {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    if (canCallApi()) {
      try {
        const response = await api.post(`/stations/${stationId}/purchases`, {
          ...data,
          id,
        });
        await stationPurchasesDB.upsert(response.data);
        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }
    const purchase = {
      id,
      station_id: stationId,
      ...data,
      // Aperçu hors ligne arrondi comme le serveur (au franc entier en FCFA):
      // 1 234,5 L à 735,5 F donnaient un coût à centimes que la ligne
      // synchronisée ne porterait jamais.
      total_cost: arrondiDevise(
        Number(data.quantity_liters || 0) * Number(data.unit_cost || 0),
        data.currency,
      ),
      purchase_date: data.purchase_date || now,
      created_at: now,
    };
    await stationPurchasesDB.upsert(purchase);
    await syncQueue.add('station_purchase', 'create', purchase);
    return purchase;
  },

  async getSales(stationId, params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get(`/stations/${stationId}/sales`, {
          params,
        });
        const items = response.data?.data || response.data || [];
        await stationSalesDB.bulkUpsert(items);
        return items;
      } catch (error) {
        console.warn('Falling back to local sales');
      }
    }
    return await stationSalesDB.getByStationId(stationId);
  },

  async getShifts(stationId, params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get(`/stations/${stationId}/shifts`, {
          params,
        });
        const items = response.data?.data || response.data || [];
        await stationShiftsDB.bulkUpsert(items);
        return items;
      } catch (error) {
        console.warn('Falling back to local shifts');
      }
    }
    return await stationShiftsDB.getByStationId(stationId);
  },

  async getShiftReadiness(stationId) {
    if (canCallApi()) {
      const response = await api.get(`/stations/${stationId}/shifts/readiness`);
      return response.data?.data || response.data;
    }
    return null;
  },

  async getExpenses(stationId, params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get(`/stations/${stationId}/expenses`, {
          params,
        });
        const items = response.data?.data || response.data || [];
        await stationExpensesDB.bulkUpsert(items);
        return items;
      } catch (error) {
        console.warn('Falling back to local expenses');
      }
    }
    return await stationExpensesDB.getByStationId(stationId);
  },
  // Lot 030 : ouvrir, clôturer un poste, saisir une vente détaillée ou une dépense
  // passent par `lib/stationsApi.js` (en ligne, clé d'idempotence) ; les anciennes
  // écritures hors ligne au format de la caisse sont retirées.
};

// Finance API
export const financeAPI = {
  async getSummary(params = {}) {
    if (canCallApi()) {
      try {
        // backend uses ?from= and ?to= aliases
        const { fromDate, toDate, from, to, ...rest } = params;
        const queryParams = {
          ...rest,
          ...(fromDate || from ? { from: fromDate || from } : {}),
          ...(toDate || to ? { to: toDate || to } : {}),
        };
        const response = await api.get('/finance/summary', { params: queryParams });
        return response.data;
      } catch (error) {
        console.error('Failed to fetch finance summary', error);
      throw error;
    }
  }
  throw new Error('Finance summary requires online connection');
  },
  async markBillingReady(tripId, comment = '') {
    const response = await api.post(`/proof-of-delivery/trips/${tripId}/billing-ready`, { comment });
    return response.data?.data || response.data;
  },
};

export const transportAPI = {
  async getTripReadiness(tripId) {
    const response = await api.get(`/transport/trips/${tripId}/readiness`);
    return response.data?.data || response.data;
  },
};

// Trips API
export const tripsAPI = {
  /**
   * Ce que cette course a coûté et rapporté.
   *
   * L'écran du trajet affichait l'avance au chauffeur, les frais de route et
   * la consommation d'espèces — jamais la recette. « Celle-ci, elle a rapporté
   * combien ? » n'avait pas de réponse dans le produit.
   */
  async getEconomics(tripId) {
    if (!canCallApi()) return null;
    try {
      const response = await api.get(`/trips/${tripId}/economics`);
      return response.data?.data || null;
    } catch (error) {
      console.warn('Failed to load trip economics');
      return null;
    }
  },

  /**
   * Les étapes proposées du trajet (par une zone, le chauffeur ou le bureau)
   * qui attendent une confirmation. Une liste vide si rien n'est à confirmer.
   */
  async getProposals(tripId) {
    if (!canCallApi()) return [];
    try {
      const response = await api.get(`/trips/${tripId}/geofence-proposals`, { skipToast: true });
      return Array.isArray(response.data?.data) ? response.data.data : [];
    } catch (error) {
      return [];
    }
  },

  /** Position apprise par téléphone: `{ zone_id }` (lieu connu) ou `{ latitude, longitude }`. Source « bureau ». */
  async reportOfficePosition(tripId, body) {
    const response = await api.post(`/trips/${tripId}/office-position`, body);
    return response.data?.data || null;
  },

  /** Confirme en un geste l'étape proposée: le trajet avance, la proposition se referme. */
  async confirmProposal(tripId, proposalId = 'open') {
    const response = await api.post(`/trips/${tripId}/geofence-proposals/${proposalId}/confirm`);
    return response.data?.data || null;
  },

  /** Les commandes que ce camion porte, et ce qu'il lui reste à vendre. */
  async getAllocations(tripId) {
    if (!canCallApi()) return null;
    try {
      const response = await api.get(`/trips/${tripId}/allocations`);
      return response.data?.data || null;
    } catch (error) {
      console.warn('Failed to load trip allocations');
      return null;
    }
  },

  async getAll(params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get('/trips', { params });
        await tripsDB.bulkUpsert(response.data);
        return response.data;
      } catch (error) {
        shouldFallbackOffline(error, 'trips');
      }
    }

    let trips = await tripsDB.getAll();
    if (params?.operation_id) {
      trips = trips.filter((t) => t?.operation_id === params.operation_id);
    }
    if (params?.truck_id) {
      trips = trips.filter((t) => t?.truck_id === params.truck_id);
    }
    return trips;
  },

  async getActive() {
    if (canCallApi()) {
      try {
        const response = await api.get('/trips/active');
        await tripsDB.bulkUpsert(response.data);
        // Le serveur renvoie aussi les trajets terminés: l'accueil les listait
        // sous « Trajets en cours » (« Terminé, 100 % ») à côté d'une tuile
        // « En route : 4 ». Même règle que la lecture hors ligne.
        return Array.isArray(response.data)
          ? response.data.filter((trip) => !['COMPLETED', 'CANCELLED'].includes(trip?.status))
          : response.data;
      } catch (error) {
        console.warn('Falling back to local data');
      }
    }
    return await tripsDB.getActive();
  },

  async getById(id) {
    if (canCallApi()) {
      try {
        const response = await api.get(`/trips/${id}`);
        await tripsDB.upsert(response.data);
        return response.data;
      } catch (error) {
        console.warn('Falling back to local data');
      }
    }
    return await tripsDB.getById(id);
  },

  async getWorkflow(id) {
    const response = await api.get(`/trips/${id}/workflow`);
    return response.data?.data || response.data;
  },

  async getWorkflowActions(id) {
    const response = await api.get(`/trips/${id}/workflow/actions`);
    return response.data?.data || response.data;
  },

  async transitionWorkflow(id, data) {
    const response = await api.post(`/trips/${id}/workflow/transition`, data);
    return response.data?.data || response.data;
  },

  async getReadiness(id) {
    return transportAPI.getTripReadiness(id);
  },

  /**
   * `corridorId` voyage en paramètre d'URL et non dans le corps, parce que
   * c'est ainsi que la route l'attend. Il vaut mieux que les deux pays: Dakar
   * compte pour deux corridors, que le couple de pays ne sait pas distinguer —
   * et c'est de lui que le serveur déduit maintenant les pays du trajet, plutôt
   * que de l'écran qui les écrivait en dur.
   */
  async create(data, { corridorId } = {}) {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();

    // Prefer server create when online to avoid duplicate initial updates.
    if (canCallApi()) {
      try {
        const response = await api.post('/trips', { ...data, id }, {
          params: corridorId ? { corridor_id: corridorId } : undefined,
        });
        await tripsDB.upsert(response.data);

        // Best-effort: update local truck status
        if (response.data?.truck_id) {
          const truck = await trucksDB.getById(response.data.truck_id);
          if (truck) {
            truck.current_status = 'ON_TRIP';
            truck.updated_at = now;
            await trucksDB.upsert(truck);
          }
        }

        return response.data;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }

    // Offline-first create
    const trip = {
      id,
      ...data,
      created_at: now,
      updated_at: now,
      status: data.status || 'CREATED',
    };
    await tripsDB.upsert(trip);

    // Update truck status locally
    if (trip.truck_id) {
      const truck = await trucksDB.getById(trip.truck_id);
      if (truck) {
        truck.current_status = 'ON_TRIP';
        truck.updated_at = now;
        await trucksDB.upsert(truck);
      }
    }

    // Initial status update locally + queued
    const initialUpdate = {
      id: crypto.randomUUID(),
      trip_id: id,
      status: 'CREATED',
      note: 'Trajet créé',
      created_at: now,
    };
    await tripUpdatesDB.add(initialUpdate);

    await syncQueue.add('trip', 'create', trip);
    await syncQueue.add('trip_update', 'create', initialUpdate);

    return trip;
  },

  
  async addExpense(id, expenseData) {
    if (canCallApi()) {
      const response = await api.post(`/trips/${id}/expenses`, expenseData);
      await tripsDB.upsert(response.data);
      return response.data;
    }
    // We could handle this offline, but for simplicity of the ledger we just force online for now or do a soft update
    throw new Error('You must be online to add itemized expenses.');
  },

  async deleteExpense(id, expenseId) {
    if (canCallApi()) {
      const response = await api.delete(`/trips/${id}/expenses/${expenseId}`);
      await tripsDB.upsert(response.data);
      return response.data;
    }
    throw new Error('You must be online to delete itemized expenses.');
  },

  async update(id, data) {
    const now = new Date().toISOString();

    if (canCallApi()) {
      try {
        const response = await api.patch(`/trips/${id}`, data);
        await tripsDB.upsert(response.data);
        return response.data;
      } catch (error) {
        const existing = await tripsDB.getById(id);
        const updated = { ...existing, ...data, updated_at: now };
        await tripsDB.upsert(updated);
        await syncQueue.add('trip', 'update', updated);
        return updated;
      }
    }

    const existing = await tripsDB.getById(id);
    const updated = { ...existing, ...data, updated_at: now };
    await tripsDB.upsert(updated);
    await syncQueue.add('trip', 'update', updated);
    return updated;
  },

  async updateStatus(tripId, statusData) {
    const now = new Date().toISOString();
    if (canCallApi()) {
      try {
        const response = await api.patch(`/trips/${tripId}/status`, null, {
          params: {
            new_status: statusData.status,
            note: statusData.note || undefined,
          },
        });
        const updatedTrip = response.data;
        await tripsDB.upsert(updatedTrip);

        const update = {
          id: crypto.randomUUID(),
          trip_id: tripId,
          status: statusData.status,
          note: statusData.note || `Status changed to ${statusData.status}`,
          created_at: now,
        };
        await tripUpdatesDB.add(update);

        return update;
      } catch (error) {
        if (error?.response) throw error;
        // transport failure: fall through to offline queue
      }
    }

    const id = crypto.randomUUID();
    const update = {
      id,
      ...statusData,
      trip_id: tripId,
      created_at: now,
    };

    await tripUpdatesDB.add(update);

    const trip = await tripsDB.getById(tripId);
    if (trip) {
      trip.status = statusData.status;
      trip.updated_at = now;
      await tripsDB.upsert(trip);
    }

    await syncQueue.add('trip_update', 'create', update);
    return update;
  },

  async delete(id) {
    const trip = await tripsDB.getById(id);
    if (trip) {
      const completedStatuses = new Set(['COMPLETED', 'CANCELLED']);
      const wasActive = !completedStatuses.has(trip.status);

      await tripUpdatesDB.deleteByTripId(id);
      await tripsDB.deleteById(id);

      if (wasActive && trip.truck_id) {
        const truck = await trucksDB.getById(trip.truck_id);
        if (truck) {
          truck.current_status = 'FREE';
          truck.updated_at = new Date().toISOString();
          await trucksDB.upsert(truck);
        }
      }
    }

    if (canCallApi()) {
      try {
        const response = await api.delete(`/trips/${id}`);
        return response.data;
      } catch (error) {
        await syncQueue.add('trip', 'delete', { id });
        throw error;
      }
    }

    await syncQueue.add('trip', 'delete', { id });
    return { id, deleted_offline: true };
  },

  async getUpdates(tripId) {
    if (canCallApi()) {
      try {
        const response = await api.get(`/trips/${tripId}/updates`);
        const updates = Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
            ? response.data
            : [];
        await tripUpdatesDB.bulkUpsert(updates);
        return updates;
      } catch (error) {
        console.warn('Falling back to local data');
      }
    }
    return await tripUpdatesDB.getByTripId(tripId);
  },

  /** Dépôts chargeurs déjà saisis par la société (suggestions et filtre). */
  async getLoadingDepots() {
    if (!canCallApi()) return [];
    try {
      const response = await api.get('/trips/loading-depots');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      return [];
    }
  },

  /**
   * Entrée (`ENTER`) ou sortie (`LEAVE`) groupée du parking. La clé rend
   * l'action rejouable sans double effet.
   */
  async setParking({ tripIds, action, at, idempotencyKey }) {
    const response = await api.post('/trips/parking', {
      trip_ids: tripIds,
      action,
      ...(at ? { at } : {}),
      idempotency_key: idempotencyKey,
    });
    return response.data;
  },

  /** Dépôt, N° de bon, date de chargement, document de sortie, sortie douane. */
  async updateLoadingInfo(tripId, data) {
    const response = await api.put(`/trips/${tripId}/loading-info`, data);
    if (response.data?.id) await tripsDB.upsert(response.data);
    return response.data;
  },

  async getAllPaged(params = {}) {
    if (canCallApi()) {
      try {
        const response = await api.get('/trips', {
          params: { ...params, include_total: true },
        });
        await tripsDB.bulkUpsert(response.data);
        return { items: response.data, total: getTotalFromHeaders(response) };
      } catch (error) {
        shouldFallbackOffline(error, 'trips');
      }
    }
    // Hors ligne: la recherche et les filtres s'appliquent aux trajets gardés
    // dans l'appareil (le serveur ne peut plus le faire). Camions et chauffeurs
    // ne sont lus que pour la recherche. La page demandée est découpée ici: sans
    // cela, la première page montrerait tous les trajets de l'appareil.
    const [trips, camions, chauffeurs] = await Promise.all([
      tripsDB.getAll(),
      params?.q ? trucksDB.getAll() : [],
      params?.q ? driversDB.getAll() : [],
    ]);
    const filtres = filtrerTrajetsLocalement(trips, params, { camions, chauffeurs });
    const page = Number(params?.page);
    const taille = Number(params?.page_size);
    if (page >= 1 && taille >= 1) {
      return { items: filtres.slice((page - 1) * taille, page * taille), total: filtres.length };
    }
    return { items: filtres, total: null };
  },
};

// Dashboard API
export const dashboardAPI = {
  async getBusinessCockpit() {
    const response = await api.get('/dashboard/business-cockpit');
    return response.data?.data || response.data;
  },
  async getStats() {
    if (canCallApi()) {
      const response = await api.get('/dashboard/stats');
      return response.data?.data || response.data;
    }
    const [trucks, trips, drivers, operations] = await Promise.all([
      trucksDB.getAll(),
      tripsDB.getActive(),
      driversDB.getActive(),
      operationsDB.getActive(),
    ]);

    return {
      trucks: {
        total: trucks.length,
        free: trucks.filter((t) => t.current_status === 'FREE').length,
        on_trip: trucks.filter((t) => t.current_status === 'ON_TRIP').length,
        maintenance: trucks.filter((t) => t.current_status === 'MAINTENANCE')
          .length,
      },
      trips: {
        active: trips.length,
        at_customs: trips.filter((t) =>
          ['AT_CUSTOMS_EXIT', 'AT_CUSTOMS_ENTRY'].includes(t.status),
        ).length,
      },
      drivers: { active: drivers.length },
      operations: { active: operations.length },
      financial: { total_completed: 0 },
    };
  },

  async getNotifications(limit = 20) {
    if (canCallApi()) {
      try {
        const response = await api.get('/notifications', { params: { limit } });
        return response.data?.data || response.data || [];
      } catch (error) {
        return [];
      }
    }
    return [];
  },
};

// Export API
export const exportAPI = {
  async downloadExcel() {
    if (!canCallApi()) throw new Error('offline');
    // Return the full axios response to access headers (filename)
    return await api.get('/export/excel', { responseType: 'blob' });
  },

  async downloadCSV(params = {}) {
    if (!canCallApi()) throw new Error('offline');
    return await api.get('/export/csv', {
      params,
      responseType: 'blob',
    });
  },
};

// Global search
export const globalSearch = async (query) => {
  if (canCallApi()) {
    try {
      const response = await api.get('/search/global', {
        params: { q: query },
      });
      return response.data;
    } catch (error) {
      console.warn('Falling back to local search');
    }
  }

  const [trucks, operations] = await Promise.all([
    trucksDB.search(query),
    operationsDB.search(query),
  ]);
  return { trucks, operations, clients: [], orders: [], trips: [] };
};

// Seed data
export const seedData = async () => {
  const response = await api.post('/demo/load-demo-data');
  return response.data;
};

// ---------------------------------------------------------------------------
// Citernes et répartition en masse
// ---------------------------------------------------------------------------

/**
 * Ce que la société appelle « une citerne ». Purement lu : l'écriture vit dans
 * l'écran de paramétrage. Ne lève jamais — un réglage injoignable ne doit pas
 * empêcher de saisir une commande, le serveur retombera sur son propre défaut.
 */
export const volumeUnitsAPI = {
  async get() {
    if (!canCallApi()) return null;
    try {
      const response = await api.get('/org-settings/volume-units', {
        skipToast: true,
      });
      return response.data?.data?.settings || null;
    } catch (error) {
      return null;
    }
  },
};

export const dispatchAPI = {
  /**
   * Une seule requête là où l'écran en envoyait N. La séquentialité qui
   * justifiait la boucle côté client — chaque allocation validée contre le
   * restant serveur — n'a pas disparu : elle est faite côté serveur, dans une
   * transaction, avec une clé d'idempotence pour que le rejeu d'un lot après
   * une coupure réseau ne double rien.
   */
  async allocateBatch(orderId, allocations, idempotencyKey) {
    const key = idempotencyKey || crypto.randomUUID();
    const payload = {
      allocations: allocations.map((item) => ({
        trip_id: item.trip_id,
        quantity: Number(item.quantity),
      })),
      idempotency_key: key,
    };

    // Sans le serveur, la passerelle locale applique le lot (lib/offline/writeRoutes.js)
    // et rejoue chaque affectation, dans l'ordre, au retour du réseau.
    const response = await api.post(
      `/orders/${orderId}/allocate-batch`,
      payload,
      // L'écran rend compte lui-même du détail appliqué/refusé.
      { skipToast: true },
    );
    return response.data?.data || response.data;
  },
};

export default api;

// Banc d'essai navigateur (scripts/offline_recette.mjs): expose le client de l'application
// pour rejouer les mêmes appels que les écrans. Inerte sauf si l'indicateur local est posé.
try {
  if (typeof window !== 'undefined' && window.localStorage?.getItem('ecarbu:e2e') === '1') {
    window.__ecarbuE2E = { api, miseAJourLocale, attendreModeleLocal, getOnlineStatus };
  }
} catch (erreur) {
  // stockage indisponible: rien à exposer
}

/**
 * Location de camions citernes.
 *
 * Les listes renvoient `{ items, pagination }`: la pagination est faite par le
 * serveur, et sans le total l'écran ne peut pas distinguer une page pleine de
 * la dernière page — il afficherait « tout » en en cachant la moitié.
 */
export const rentalsAPI = {
  async getKpis(periodDays = 30) {
    return unwrap(await api.get('/rentals/kpis', { params: { period_days: periodDays } }));
  },

  // Contrats
  async getContracts(params = {}) {
    const response = await api.get('/rentals/contracts', { params });
    return {
      items: response.data?.data || [],
      pagination: response.data?.pagination || null,
    };
  },
  async getContract(id) {
    return unwrap(await api.get(`/rentals/contracts/${id}`));
  },
  async createContract(data) {
    return unwrap(await api.post('/rentals/contracts', data));
  },
  async updateContract(id, data) {
    return unwrap(await api.patch(`/rentals/contracts/${id}`, data));
  },
  async activateContract(id) {
    return unwrap(await api.post(`/rentals/contracts/${id}/activate`));
  },
  async suspendContract(id, note) {
    return unwrap(await api.post(`/rentals/contracts/${id}/suspend`, { note }));
  },
  async closeContract(id, actualReturnDate) {
    return unwrap(
      await api.post(`/rentals/contracts/${id}/close`, {
        actual_return_date: actualReturnDate || null,
      }),
    );
  },
  async getBillableUnits(id, params = {}) {
    return unwrap(await api.get(`/rentals/contracts/${id}/billable-units`, { params }));
  },

  // Périodes
  async getPeriods(contractId) {
    return unwrap(await api.get(`/rentals/contracts/${contractId}/periods`));
  },
  async generatePeriods(contractId, until) {
    return unwrap(
      await api.post(`/rentals/contracts/${contractId}/periods:generate`, {
        until: until || null,
      }),
    );
  },
  async updatePeriod(periodId, data) {
    return unwrap(await api.patch(`/rentals/periods/${periodId}`, data));
  },

  // Rotations
  async getRotations(contractId) {
    return unwrap(await api.get(`/rentals/contracts/${contractId}/rotations`));
  },
  async createRotation(contractId, data) {
    return unwrap(await api.post(`/rentals/contracts/${contractId}/rotations`, data));
  },
  async updateRotation(rotationId, data) {
    return unwrap(await api.patch(`/rentals/rotations/${rotationId}`, data));
  },
  async validateRotation(rotationId) {
    return unwrap(await api.post(`/rentals/rotations/${rotationId}/validate`));
  },
  async deleteRotation(rotationId) {
    return unwrap(await api.delete(`/rentals/rotations/${rotationId}`));
  },

  // États des lieux
  async getInspections(contractId) {
    return unwrap(await api.get(`/rentals/contracts/${contractId}/inspections`));
  },
  async recordInspection(contractId, kind, data) {
    return unwrap(await api.post(`/rentals/contracts/${contractId}/inspections/${kind}`, data));
  },
  async closeWithCheckin(contractId, data) {
    return unwrap(await api.post(`/rentals/contracts/${contractId}/checkin`, data));
  },

  // Charges
  async getCharges(contractId) {
    return unwrap(await api.get(`/rentals/contracts/${contractId}/charges`));
  },
  async createCharge(contractId, data) {
    return unwrap(await api.post(`/rentals/contracts/${contractId}/charges`, data));
  },
  async updateCharge(chargeId, data) {
    return unwrap(await api.patch(`/rentals/charges/${chargeId}`, data));
  },
  async deleteCharge(chargeId) {
    return unwrap(await api.delete(`/rentals/charges/${chargeId}`));
  },
};

// --------------------------------------------------------------------------
// Lot 8 — rendre lisibles les algorithmes déjà calculés côté serveur.
// Ajouté en fin de fichier pour ne pas déplacer les exports existants.
// --------------------------------------------------------------------------

/** Score d'intégrité d'un trajet: lecture seule, puis recalcul explicite. */
export const tripIntegrityAPI = {
  // La lecture ne doit rien déclencher: le recalcul écrit au ledger et peut
  // lever une alerte, donc il reste un geste de l'opérateur.
  async getScore(tripId) {
    const response = await api.get(`/mission-integrity/trips/${tripId}`, {
      skipToast: true,
    });
    return response.data?.data ?? null;
  },
  async recompute(tripId) {
    const response = await api.post(`/mission-integrity/trips/${tripId}/recompute`);
    return response.data?.data || response.data;
  },
};

/** Écarts de volume et leur trace de décision. */
export const volumeReconciliationsAPI = {
  async getAll() {
    const response = await api.get('/volume-reconciliations', { skipToast: true });
    return response.data?.data || [];
  },
  /**
   * Une page d'écarts (`page`, `page_size`, `status`, `trip_id`, `q`): les
   * lignes, le total filtré et le nombre d'écarts en attente de décision dans
   * toute la société (`pendingCount`).
   */
  async getPage(params = {}) {
    const response = await api.get('/volume-reconciliations', { params, skipToast: true });
    const items = response.data?.data || [];
    const meta = response.data?.meta || {};
    return {
      items,
      total: getTotalFromHeaders(response) ?? meta.total ?? items.length,
      pendingCount: meta.pending_count ?? null,
    };
  },
  async getDecisions(reconciliationId) {
    const response = await api.get(
      `/volume-reconciliations/${reconciliationId}/decisions`,
      { skipToast: true },
    );
    return response.data?.data || [];
  },
  async approve(reconciliationId, { comment, reason } = {}) {
    const response = await api.post(
      `/volume-reconciliations/${reconciliationId}/approve`,
      { comment, reason },
    );
    return response.data?.data || response.data;
  },
  async reject(reconciliationId, { comment, reason } = {}) {
    const response = await api.post(
      `/volume-reconciliations/${reconciliationId}/reject`,
      { comment, reason },
    );
    return response.data?.data || response.data;
  },
};

/** Maillons du ledger opérationnel, du plus récent au plus ancien. */
export const ledgerEntriesAPI = {
  async getRecent(limit = 50) {
    const response = await api.get('/operational-ledger/entries', {
      params: { limit },
      skipToast: true,
    });
    return response.data?.data || [];
  },
};

/** Colonnes kanban construites par le moteur de workflow serveur. */
export const transportBoardAPI = {
  async getKanban() {
    const response = await api.get('/transport/kanban', { skipToast: true });
    return response.data?.data || [];
  },
};

/**
 * Lot 7 — facturation des contrats de location.
 *
 * Séparée d'`invoicesAPI` parce que la matière facturée n'a rien à voir: une
 * location n'a ni commande ni bon de livraison, elle facture des tranches de
 * loyer, des rotations, du carburant et des dégâts. Les factures produites
 * atterrissent en revanche dans la même liste, avec le même encours client.
 *
 * L'aperçu existe pour éviter le « j'émets puis j'annule »: une annulation
 * laisse un trou dans la numérotation et une trace dans l'historique client.
 */
export const rentalInvoicesAPI = {
  async getForContract(contractId) {
    return unwrap(await api.get(`/rentals/contracts/${contractId}/invoices`));
  },
  async preview(contractId, { dateFrom, dateTo, includeCharges = true } = {}) {
    return unwrap(
      await api.post(`/rentals/contracts/${contractId}/invoices:preview`, {
        date_from: dateFrom || null,
        date_to: dateTo || null,
        include_charges: includeCharges,
      }),
    );
  },
  async generate(contractId, { dateFrom, dateTo, includeCharges = true, idempotencyKey } = {}) {
    return unwrap(
      await api.post(`/rentals/contracts/${contractId}/invoices`, {
        date_from: dateFrom || null,
        date_to: dateTo || null,
        include_charges: includeCharges,
        // La clé rend le double-clic et la reprise réseau inoffensifs: la
        // seconde requête reçoit la première facture, elle n'en crée pas une.
        idempotency_key: idempotencyKey || null,
      }),
    );
  },
};

/**
 * Lot 6 — impressions d'exploitation.
 *
 * Seules les URL vivent ici: les documents ne se lisent pas en JSON, ils se
 * téléchargent. `PrintMenu` passe par `downloadFromApi`, qui sait déjà lire le
 * nom de fichier renvoyé par le serveur — le refaire ici dupliquerait la seule
 * partie fragile.
 *
 * Le format demandé voyage en paramètre `format` (pdf | xlsx | csv), jamais
 * dans le chemin: un même document, trois rendus.
 */
export const printingAPI = {
  trips: '/print/trips',
  tripsInTransit: '/print/trips-in-transit',
  convoy: (operationId) => `/print/operations/${operationId}/convoy`,
  trucksByStatus: '/print/trucks-by-status',
  /**
   * Envoie au responsable (administrateurs de la société, sur WhatsApp) le
   * PDF de la liste des trajets avec exactement ces filtres.
   */
  async sendTrips(params, idempotencyKey) {
    const response = await api.post('/print/trips/send', {
      ...params,
      idempotency_key: idempotencyKey,
    });
    return response.data;
  },
};

/**
 * Les barèmes tarifaires de la société.
 *
 * `resolve` répond à la seule question que les écrans posaient jusqu'ici à
 * l'exploitant: « combien le litre ? ». La route existait depuis le lot
 * précédent et **aucun écran ne l'appelait** — le prix était retapé à chaque
 * commande, et la création d'un trajet allait jusqu'à coder `725` en dur.
 *
 * Rendre `null` est une réponse normale: aucun barème ne couvre ce contexte,
 * l'exploitant saisit son prix. Ce n'est pas une erreur, et ça ne doit donc
 * produire aucun message.
 */
export const pricingRulesAPI = {
  async resolve({ clientId, clientType, stationId, operationType } = {}) {
    if (!canCallApi()) return null;
    try {
      const response = await api.get('/pricing-rules/resolve', {
        params: {
          ...(clientId ? { client_id: clientId } : {}),
          ...(clientType ? { client_type: clientType } : {}),
          ...(stationId ? { station_id: stationId } : {}),
          ...(operationType ? { operation_type: operationType } : {}),
        },
      });
      return response.data?.data || null;
    } catch (error) {
      return null;
    }
  },
};

// Écritures financières et dossiers: l'argent ne passe jamais par l'ancienne file
// /sync. La passerelle locale (lib/offline/writeRoutes.js) ne retient que ce qui
// peut s'appliquer sans le serveur (frais); le reste attend le serveur et
// échoue comme n'importe quelle requête.
export async function durableBusinessPost(entityType, action, url, data) {
  return api.post(url, data);
}
